import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../CloseButton/CloseButton";
import { normalizeGuid, guidEquals } from "../../utils/guid";
import styles from "./ProjectConfigModal.module.css";
// DEV-920 Layer 2 Step 3 follow-up. Reuse the GeoLocationPicker CSS
// module for the hierarchy picker so the inline-pill UX (token inside
// the input wrap, "Type to change…" placeholder when a value is set,
// × button on the token itself) matches Account Settings exactly.
// Importing the module rather than duplicating the styles keeps both
// pickers in lockstep — any future style tweak lands in one place.
import pickerStyles from "../GeoLocationPicker/GeoLocationPicker.module.css";

import L from "leaflet";

import { kpiProfiles } from "../../Data/ProjectConfig/kpiProfiles";
import { kpiCatalog, categoryLabels, type KpiCategoryKey, type KpiChartType } from "../../Data/ProjectConfig/kpiCatalog";
import { templateForProfileGuid } from "../../Data/ProjectConfig/kpiProfileTemplates";
import { getProjectKpiConfigOverride, upsertProjectKpiConfigOverride, type SubKpiInstance } from "../../Data/ProjectConfig/projectKpiConfigOverrides";
import { CurrencyIcon, DataReliabilityIcon, MissionIcon, ScheduleIcon } from "../TableIcons";
import MissionOutcomeFields, { type MissionOutcomeFieldValues } from "./MissionOutcomeFields";
// Phase 3 redesign (DEV-814): new Mission Sub-KPI wizard.
// Renders only for the Mission category; other categories continue to use
// the existing generic form below until they get their own per-category
// redesigns. Both components use NAMED exports (not default).
// MissionKpiListRow integration is deferred — it doesn't yet include the
// reorder controls that the existing row provides.
import { MissionKpiWizard } from "./MissionKpiWizard";
// DEV-840: Schedule wizard + list row (Mission-parity for Schedule sub-KPIs).
import { ScheduleKpiWizard } from "./ScheduleKpiWizard";
import { ScheduleKpiListRow } from "./ScheduleKpiListRow";
// DEV-816: Mission KPI wizard now mounts inside a slide-in tray instead
// of the centered modal, so the parent project's KPI list stays visible
// while a single sub-KPI is being edited.
import { Tray } from "../Tray/Tray";
import { ReportingCadenceWidget, DEFAULT_CADENCE_VALUE, type CadenceValue } from "../ReportingCadenceWidget/ReportingCadenceWidget";
import { ALL_CURRENCIES, getCurrency } from "../../Data/Budget/currencyLookup";
// import KpiProgressPanel from "./KpiProgressPanel"; // Commented out — see note at render site

import {
  setProjectOverride,
  isProjectWorkflowEnabled,
  setProjectWorkflowEnabled,
  type ProjectConfigOverride,
  type ProjectOwnerType,
  // DEV-1463 — the project visibility standard (mini-project model).
  // `Visibility` (public|private) and `ProjectPublicScope` are retired here.
  type ProjectVisibility,
  PROJECT_VISIBILITY_ORDINAL,
} from "../../Data/ProjectConfig/projectConfigOverrides";
import { canAdminProject } from "../../security/securityAdapter";
// DEV-1039 / DEV-1040 / DEV-1038 / DEV-1041. The Access tab is fully V2 now:
//   - Owners & Admins (governance grants) — DEV-1039
//   - Permission Sets (bundle authoring) — DEV-1038
//   - V1 ACL editor — deleted in DEV-1040
//   - ProjectPermissionsV2Panel mount — retired in DEV-1041 (this commit)
import ProjectOwnersAdminsPanel from "../ProjectOwnersAdminsPanel/ProjectOwnersAdminsPanel";
import ProjectBundleAuthoringPanel from "../ProjectBundleAuthoringPanel/ProjectBundleAuthoringPanel";
import {
  fetchAllCommunityEntitiesAdmin,
  fetchAllRelationships,
  fetchAllMetadataValues,
  batchUpsertProjectCustomMetadata,
  communityEntityToSeedShape,
  relationshipToSeedShape,
} from "../../api/communityMetadata";
import type { UpsertProjectMetadataValueRequest } from "../../api/communityMetadata";
import {
  projectCategoryOptions,
  projectStatusOptions,
  contentStatusOptions,
  localizedProjectCategory,
  localizedProjectStatus,
  localizedContentStatus,
} from "../../Data/ProjectConfig/projectStandardOptions";

// DEV-932: collectionSchemas + collectionSchemaFields now read from
// useApiCommunityData (apiCommunityData.schemas / .schemaFields). See
// hook destructure inside the component body.
import { useApiCommunityData } from "../../api";
import { computeMetadataNeedsValuesCount } from "../../Data/CommunityMetadata/metadataNeedsValues";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import {
  replaceProjectCollections,
} from "../../api";
// POLICY_PROJECT_OWNERSHIP_TRANSFER.md Phase 2 — owner change is no longer
// carried in the generic PUT; it goes through the dedicated transfer endpoint.
import { transferProjectOwnership } from "../../api/projects";
// DEV-932 Phase 5b regression fix. The local readSimUserGuid() in this
// file returns "" when there's no ?user= URL param (the seed-flatfile
// fallback was removed, leaving an unreachable "caller must pass list"
// branch). getCurrentUserGuid is the canonical helper from the API
// client: returns the JWT-authenticated user when available, falls back
// to the URL param, and finally to DEFAULT_USER_GUID. Same chain the
// rest of the app uses for identity resolution.
import { getCurrentUserGuid } from "../../api/client";
// DEV-918 followup: removed static `projectCustomMetadataValues` import. The
// modal now seeds `customValues` from localStorage drafts + the API. The
// static flatfile carried legacy hand-crafted value GUIDs (e.g.
// 30000000-...001013 for Rotary clubs) which were silently round-tripped
// back to the batch-upsert endpoint and rejected by the schema-validation
// check ("does not resolve to a community entity of type rotary_club"),
// because those GUIDs do not exist in the API community_entities table.
// API + localStorage is the source of truth.
// DEV-932: collections flatfile import removed; the picker grid below
// now reads apiCommunityData.collections.
// DEV-918: removed static `customEntities` import. The picker now sources
// from API-loaded `communityEntities` filtered to org_rotary_* (see
// memoized `customEntities` const below). The static seed at
// `Data/Collections/customEntities.ts` carried legacy hand-crafted GUIDs
// that diverged from migration 132's clean md5-derived GUIDs in the DB —
// the validator rejected every metadata save because the captured guid
// didn't exist in community_entities.
import { pathDisplay, pathFromLeafGuid } from "../../utils/entityPath";
import {
  searchGeoLocations,
  getGeoLocationLabel,
} from "../../utils/geoLocationSearch";
// DEV-932: project ACL + collection links now flow through the API.
// Reads come from useApiSecurityData / useApiCommunityData hooks below;
// writes go through replaceProjectAcl / replaceProjectCollections (the
// new bulk-replace endpoints) inside handleSave.
// DEV-1047 — Removed addOrUpdateWorkflow + WorkflowRequest imports.
// Their only consumer was the dead V1 ACL save path.
// Pre-flight template selection removed — templates are applied via the
// PreflightChecklist component on the Work tab, which goes through
// the proper API persistence pipeline.

// Community fixtures — static imports for Vite compatibility (replaces CRA require())
// DEV-932: communityEntities + communityEntityRelationships flatfile imports
// removed. State below now seeds from [] and is populated by the existing
// API fetch (fetchAllCommunityEntitiesAdmin / fetchAllRelationships).
// DEV-932: communityRoleAssignments + securityUsers now from
// useApiSecurityData (see hook destructure inside the component).
// DEV-910: system-admin bypass + inherited-admin walk in eligibleOwnerCommunities.
import { isUserSystemAdmin } from "../../security/securityAdapter";
// Phase 1 of POLICY_PROJECT_OWNERSHIP_TRANSFER.md — V2 capability union so
// users provisioned via the new Permissions panel (post-DEV-1050 cutover)
// appear in the owner dropdown even when they hold no legacy V1 role rows.
import { useV2CommunityRoles } from "../../security/v2/useV2CommunityRoles";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  project: any;
  onSaved: () => void;
  /**
   * DEV-841 Path 1 vestige — kept on the prop type for back-compat but
   * unused on the new Path A flow. Thermometer pencils now route through
   * <KpiWizardLauncher /> which mounts at the page level and skips
   * ProjectConfigModal entirely. Safe to remove this prop in a future
   * cleanup pass after confirming no external caller still uses it.
   */
  initialKpiEdit?: { category: KpiCategoryKey; instanceGuid: string } | null;
};

type TabKey = "basics" | "metadata" | "kpi" | "access" | "advanced";

type FieldValue = {
  value_string?: string;
  value_guid?: string;
  value_number?: number;
  value_boolean?: boolean;
  value_date?: string;
  value_enum?: string;
};

type FieldValueMap = Record<string, FieldValue>; // keyed by field_guid

const LS_CUSTOM_META_KEY = "l4e_project_custom_metadata_overrides";


const safeGuid = (v: any): string => normalizeGuid(v);

const isGeoEntity = (e: any): boolean => String(e?.entity_type ?? "").toLowerCase().startsWith("geo_");
const isOrgEntity = (e: any): boolean => {
  const t = String(e?.entity_type ?? "").toLowerCase();
  if (!t) return false;
  if (t.startsWith("geo_")) return false;
  // DEV-910: accept the legacy single-tier "organization" name in addition to
  // the org_*-prefixed taxonomy from migration 126. Org *roots* (Rotary
  // International, the per-user solo_creator_workshop rows from migration 137)
  // carry entity_type="organization" literally; org *levels* and *leaves*
  // (org_zone, org_district, org_rotary_club) follow the newer convention.
  // Excluding "organization" silently filtered out every org root from the
  // owner-transfer dropdown even when the user held a direct owner role.
  return t.startsWith("org_") || t === "org" || t === "organization";
};

/**
 * @deprecated DEV-932 Phase 5b regression. Returns "" when there's no
 * ?user= URL param (the seed-flatfile fallback was removed without
 * wiring an alternative). Callers must use `getCurrentUserGuid()` from
 * `src/api/client.ts` instead — that helper has the proper fallback
 * chain (JWT-authenticated user → URL param → DEFAULT_USER_GUID) and
 * matches the identity used by the API client's authenticated requests.
 * Kept temporarily to avoid a noisy diff if any in-flight branches
 * reference it; remove once we confirm no other call sites exist.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const readSimUserGuid = (): string => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlUser = urlParams.get("user");
    if (urlUser && urlUser.trim()) return urlUser.trim();
  } catch {
    // ignore
  }
  return "";
};

// DEV-932: takes securityUsers as a parameter. Module-level helper can't
// call the hook itself; the component passes its hook-sourced list in.
const getUserDisplayName = (
  user_guid: string,
  securityUsers: any[] = [],
): string => {
  if (!Array.isArray(securityUsers) || securityUsers.length === 0) return "Me";
  const ug = String(user_guid).toUpperCase();
  const u = securityUsers.find(
    (x: any) => String(x?.user_guid ?? x?.user_GUID ?? "").toUpperCase() === ug
  );
  const name =
    String(u?.display_name ?? u?.name ?? u?.email ?? "").trim() || String(user_guid).slice(0, 8);
  return name;
};

const safeJsonParse = <T,>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const loadCustomMetaOverrides = (): Record<string, FieldValueMap> => {
  try {
    const raw = window?.localStorage?.getItem(LS_CUSTOM_META_KEY) ?? null;
    const parsed = safeJsonParse<Record<string, FieldValueMap>>(raw, {});
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const saveCustomMetaOverrides = (all: Record<string, FieldValueMap>) => {
  try {
    window?.localStorage?.setItem(LS_CUSTOM_META_KEY, JSON.stringify(all));
  } catch {
    // ignore
  }
};

export default function ProjectConfigModal({ isOpen, onClose, project, onSaved, initialKpiEdit }: Props) {
  // DEV-932: API-sourced reads for schemas, fields, collection-project
  // links, security users, community roles, and project ACL. Replaces
  // every flatfile import that lived in src/Data/Collections/* and
  // src/Data/Security/*. refetch helpers run after writes so the UI
  // reflects fresh DB state without a page reload.
  const apiCommunityData = useApiCommunityData();

  // DEV-1039 iteration. Build a GUID→name map of community entities so the
  // Owners & Admins identity preview card can resolve a user's home
  // community (primaryLocationEntityGuid) to a readable label. Same source
  // of truth the rest of the modal uses for community lookups.
  const communityNameByGuid = useMemo(() => {
    const m = new Map<string, string>();
    const entities = apiCommunityData.communityEntities || [];
    for (const e of entities) {
      const guid = normalizeGuid((e as any)?.entity_guid);
      const name = String((e as any)?.name ?? "").trim();
      if (guid && name) m.set(guid, name);
    }
    return m;
  }, [apiCommunityData.communityEntities]);

  const {
    securityUsers: apiSecurityUsers,
    communityRoles: apiCommunityRoles,
    projectAcl: apiProjectAcl,
    refetch: refetchSecurity,
  } = useApiSecurityData();
  const { t } = useAppTranslation("projectConfig");
  const projectGuid: string = safeGuid(project?.project_GUID ?? project?.project_guid);
  // DEV-932 Phase 5b regression fix. Was: useMemo(() => readSimUserGuid(), [])
  // which returned "" with no ?user= URL param. That broke the Owner
  // dropdown (eligibleOwnerCommunities filters by currentUserGuid; empty
  // user yields empty list) and the "Me (DisplayName)" label (lookup
  // against apiSecurityUsers misses on empty key, parens render empty).
  // getCurrentUserGuid() resolves via the same chain as the API client
  // so identity is consistent across app components and authenticated
  // requests.
  const currentUserGuid = useMemo(() => getCurrentUserGuid(), []);

  const [activeTab, setActiveTab] = useState<TabKey>("basics");

  /* DEV-1039 iter 3. Access tab IA split — three subtabs replace the
     stacked sections. Default "visibility" because Visibility & Admins
     is where ~90% of admin work happens. Collections is periodic;
     Permission Sets is power-user only. */
  const [accessSubTab, setAccessSubTab] = useState<"visibility" | "collections" | "bundles">("visibility");

  // Basics
  const [projectName, setProjectName] = useState<string>("");
  const [description, setDescription] = useState<string>("");

  const [projectStartDate, setProjectStartDate] = useState<string>("");
  const [projectEndDate, setProjectEndDate] = useState<string>("");

  const [ownerType, setOwnerType] = useState<ProjectOwnerType>("user");
  const [ownerGuid, setOwnerGuid] = useState<string>("");

  // Advanced tab
  const [scheduleStartDate, setScheduleStartDate] = useState<string>("");
  const [reportingCadence, setReportingCadence] = useState<string>("");
  const [reportingScheduleValue, setReportingScheduleValue] = useState<CadenceValue>(DEFAULT_CADENCE_VALUE);
  const [ganttColorTheme, setGanttColorTheme] = useState<string>("match_theme");
  // DEV-563: Working days of the week (ISO 8601: 1=Mon..7=Sun). null = 7-day calendar.
  const [workWeek, setWorkWeek] = useState<number[] | null>(null);

  // DEV-576: Budget & Currency
  const [budgetEstimate, setBudgetEstimate] = useState<string>("");
  const [budgetCurrency, setBudgetCurrency] = useState<string>("USD");

  // Community entities & relationships — fetched from API (DEV-932: was
  // seeded from static flatfile; now empty until the API fetch resolves).
  const [communityEntities, setCommunityEntities] = useState<any[]>([]);
  const [communityEntityRelationships, setCommunityEntityRelationships] = useState<any[]>([]);

  // Primary location (geo)
  const [primaryLocationGuid, setPrimaryLocationGuid] = useState<string>("");
  const [primaryLocationQuery, setPrimaryLocationQuery] = useState<string>("");
  const [showLocationSuggestions, setShowLocationSuggestions] = useState<boolean>(false);

  // Map point (lat/lon)
  const [locationLat, setLocationLat] = useState<string>("");
  const [locationLon, setLocationLon] = useState<string>("");

  // Mini map (Basics tab) — click/drag to set lat/lon
  const miniMapContainerRef = useRef<HTMLDivElement | null>(null);
  const miniMapRef = useRef<L.Map | null>(null);
  const miniMarkerRef = useRef<L.Marker | null>(null);

  // Migration 039 / DEV-822 — HSE mission rows are platform-managed and
  // must never appear in the sub-KPI configuration wizard. We split them
  // out of `draftKpiInstances.mission` on load (so the wizard renders,
  // reorders, weights, and persists only user-managed rows) and merge
  // them back into the override on save (so the engine and HSE bundle
  // scorer continue to see them via the local store). The API itself is
  // never touched on HSE rows by this wizard — sync filters them out and
  // the server PATCH/DELETE guards reject them.
  const heldHseMissionRowsRef = useRef<SubKpiInstance[]>([]);

  // ── Visibility (DEV-1463 — the project standard; migration 275) ──
  //
  // Rebuilt on the mini-project model (Skip, 2026-07-13): one three-value
  // ladder, plus an independent Progress dial capped at it. This replaces
  // DEV-1039's public|private + public_scope pair, which encoded the same
  // ladder across two fields and never actually persisted.
  //
  // Visibility STAYS in Project Settings through the settings redesign
  // (Skip: "that is one thing that won't move").
  const [visibility, setVisibility] = useState<ProjectVisibility>("team_only");
  const [progressVisibility, setProgressVisibility] = useState<ProjectVisibility>("team_only");
  const [isHidden, setIsHidden] = useState<boolean>(false);

  // DEV-1047 — Removed onlyMe, draftAcl, permissionsModified state. The V1
  // ACL editor was deleted in DEV-1040; the save path that consumed these
  // was gated on permissionsModified which could no longer become true.
  // Governance grants are owned by ProjectOwnersAdminsPanel (DEV-1039).

  // Force re-render when workflow overrides are toggled.
  const [workflowToggleVersion, setWorkflowToggleVersion] = useState(0);

  // Can the current user edit project settings? System admins always can;
  // otherwise, defer to the V2-routed admin authority check. Post-DEV-1047
  // we no longer consult the V1 draftAcl — that state was always empty.
  const canEditProject = useMemo(() => {
    const me = String(currentUserGuid || "");
    if (!me) return false;
    return canAdminProject(me, projectGuid);
  }, [currentUserGuid, projectGuid]);

  const projectWorkflowOptions = useMemo(
    () => [
      {
        action: "project.kpi_progress_update",
        labelKey: "advanced.workflowKpiLabel",
        descKey: "advanced.workflowKpiDesc",
      },
      {
        action: "project.permissions_change",
        labelKey: "advanced.workflowPermissionsLabel",
        descKey: "advanced.workflowPermissionsDesc",
      },
      {
        action: "project.role_assignment",
        labelKey: "advanced.workflowRoleLabel",
        descKey: "advanced.workflowRoleDesc",
      },
    ],
    [],
  );
  const [draftCollections, setDraftCollections] = useState<string[]>([]);

  // Standard meta
  const [projectCategory, setProjectCategory] = useState<string>("");
  const [projectStatus, setProjectStatus] = useState<string>("");
  const [contentStatus, setContentStatus] = useState<string>("");

  // Config selections
  const [metadataSchemaGuid, setMetadataSchemaGuid] = useState<string>("");
  const [kpiProfileGuid, setKpiProfileGuid] = useState<string>("");
  // Audit trail toggle (project-level)
  const [auditEnabled, setAuditEnabled] = useState<boolean>(false);
// KPI Configuration (project-level overrides)
const DEFAULT_CATEGORY_WEIGHTS: Record<KpiCategoryKey, number> = {
  schedule: 25,
  finance: 25,
  mission: 25,
  data_reliability: 25,
};

const [draftCategoryWeights, setDraftCategoryWeights] = useState<Record<KpiCategoryKey, number>>(DEFAULT_CATEGORY_WEIGHTS);
const [draftKpiInstances, setDraftKpiInstances] = useState<Record<KpiCategoryKey, SubKpiInstance[]>>({
  schedule: [],
  finance: [],
  mission: [],
  data_reliability: [],
});

// Selection within a category list (for reorder buttons)
const [selectedKpiIndexByCategory, setSelectedKpiIndexByCategory] = useState<Record<KpiCategoryKey, number>>({
  schedule: -1,
  finance: -1,
  mission: -1,
  data_reliability: -1,
});

// Add/Edit SubKPI modal state (simple v1)
const [isAddKpiOpen, setIsAddKpiOpen] = useState(false);
const [addKpiCategory, setAddKpiCategory] = useState<KpiCategoryKey>("schedule");
const [addKpiCatalogGuid, setAddKpiCatalogGuid] = useState<string>("kpi_sched_progress_v1");
const [addKpiDisplayName, setAddKpiDisplayName] = useState<string>("");
const [addKpiDisplayMode, setAddKpiDisplayMode] = useState<"full" | "mini">("full");
const [addKpiDirection, setAddKpiDirection] = useState<"increase_is_good" | "decrease_is_good">("increase_is_good");
const [addKpiGreenThreshold, setAddKpiGreenThreshold] = useState<number>(80);
const [addKpiYellowThreshold, setAddKpiYellowThreshold] = useState<number>(60);
const [addKpiVarianceYellowPP, setAddKpiVarianceYellowPP] = useState<number>(5);
const [addKpiVarianceRedPP, setAddKpiVarianceRedPP] = useState<number>(10);
const [addKpiMainChart, setAddKpiMainChart] = useState<KpiChartType>("bar");
const [addKpiHistoryChart, setAddKpiHistoryChart] = useState<KpiChartType>("line");
const [addKpiBindingNote, setAddKpiBindingNote] = useState<string>("");
const [addKpiScheduleTemplateType, setAddKpiScheduleTemplateType] = useState<"progress" | "progress_spi" | "progress_cpi">("progress");
const [addKpiSpiWarnThreshold, setAddKpiSpiWarnThreshold] = useState<number>(0.95);
const [addKpiSpiCritThreshold, setAddKpiSpiCritThreshold] = useState<number>(0.90);
const [addKpiSpiMinBaselinePct, setAddKpiSpiMinBaselinePct] = useState<number>(5);
const [addKpiSpiWeightPct, setAddKpiSpiWeightPct] = useState<number>(50);
const [showAdvancedKpiFields, setShowAdvancedKpiFields] = useState(false);
const [editingSubKpiInstanceGuid, setEditingSubKpiInstanceGuid] = useState<string>("");

// Mission Outcome fields state
const defaultMissionOutcomeValues: MissionOutcomeFieldValues = {
  outcome_id: "",
  value_type: "numeric",
  tracking_mode: "cumulative",
  direction: "increase_is_good",
  uom: "count",
  cadence: "monthly",
  target_goal: undefined,
  target_baseline: undefined,
  milestone_total: undefined,
  display_name: "",
  tracking_start_date: "",
  deadline_date: "",
  warning_days: undefined,
  critical_days: undefined,
};
const [missionOutcomeValues, setMissionOutcomeValues] = useState<MissionOutcomeFieldValues>(defaultMissionOutcomeValues);

const kpiTemplate = useMemo(() => templateForProfileGuid(kpiProfileGuid || null), [kpiProfileGuid]);

const effectiveCategoryWeights = useMemo(() => {
  return kpiTemplate ? kpiTemplate.category_weights : draftCategoryWeights;
}, [kpiTemplate, draftCategoryWeights]);

const mandatoryCatalogByCategory = useMemo(() => {
  const m = new Map<KpiCategoryKey, Set<string>>();
  (["schedule", "finance", "mission", "data_reliability"] as KpiCategoryKey[]).forEach((k) => m.set(k, new Set()));
  if (kpiTemplate?.mandatory_catalog_kpis) {
    (Object.keys(kpiTemplate.mandatory_catalog_kpis) as KpiCategoryKey[]).forEach((k) => {
      const arr = kpiTemplate.mandatory_catalog_kpis?.[k] || [];
      const set = m.get(k) || new Set<string>();
      arr.forEach((id) => set.add(String(id)));
      m.set(k, set);
    });
  }
  return m;
}, [kpiTemplate]);

  // Fetch community entities & relationships from API when modal opens.
  // This ensures newly-added sub-communities appear in the Primary Location search.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [apiEntities, apiRels] = await Promise.all([
          fetchAllCommunityEntitiesAdmin(),
          fetchAllRelationships(),
        ]);
        if (cancelled) return;
        if (Array.isArray(apiEntities) && apiEntities.length > 0) {
          setCommunityEntities(apiEntities.map(communityEntityToSeedShape));
        }
        if (Array.isArray(apiRels) && apiRels.length > 0) {
          setCommunityEntityRelationships(apiRels.map(relationshipToSeedShape));
        }
      } catch {
        // API unavailable — keep static fallback data (already set as initial state)
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  // When a KPI Profile is selected/changed, auto-seed mandatory SubKPIs into the draft instances
  // so they persist on Save (and testers don't have to rebuild the baseline each time).
  useEffect(() => {
    if (!isOpen) return;
    const tpl = templateForProfileGuid(kpiProfileGuid || null);
    if (!tpl) return;

    // Apply template weights into the draft (effective weights come from the template anyway).
    setDraftCategoryWeights({
      ...DEFAULT_CATEGORY_WEIGHTS,
      ...(tpl.category_weights as any),
    });

    // Ensure mandatory KPI instances exist in the project draft lists.
    setDraftKpiInstances((prev) => ({
      schedule: ensureMandatoryInstances("schedule", prev.schedule || []),
      finance: ensureMandatoryInstances("finance", prev.finance || []),
      mission: ensureMandatoryInstances("mission", prev.mission || []),
      data_reliability: ensureMandatoryInstances("data_reliability", prev.data_reliability || []),
    }));
  }, [isOpen, kpiProfileGuid]);


function normalizeMandatoryFlags(category: KpiCategoryKey, instances: SubKpiInstance[]) {
  const mandatory = mandatoryCatalogByCategory.get(category) || new Set<string>();
  const out: SubKpiInstance[] = [];
  const seenMandatory = new Set<string>();

  (instances || []).forEach((inst) => {
    const cg = String(inst.catalog_kpi_guid);

    if (mandatory.has(cg)) {
      // First instance of a mandatory catalog KPI is mandatory; duplicates are not.
      if (!seenMandatory.has(cg)) {
        seenMandatory.add(cg);
        out.push({ ...inst, is_mandatory: true });
      } else {
        out.push({ ...inst, is_mandatory: false });
      }
      return;
    }

    // Not mandatory under the current profile template.
    out.push({ ...inst, is_mandatory: false });
  });

  return out;
}

function categoryIconFor(cat: KpiCategoryKey) {
  switch (cat) {
    case "schedule":
      return <ScheduleIcon color="currentColor" />;
    case "finance":
      return <CurrencyIcon color="currentColor" />;
    case "mission":
      return <MissionIcon color="currentColor" />;
    case "data_reliability":
      return <DataReliabilityIcon color="currentColor" />;
    default:
      return null;
  }
}

function makeInstanceGuid() {
  try {
    // modern browsers
    // @ts-ignore
    if (typeof crypto !== "undefined" && crypto?.randomUUID) return crypto.randomUUID();
  } catch {}
  return String(Date.now()) + "_" + Math.random().toString(16).slice(2);
}

function ensureMandatoryInstances(category: KpiCategoryKey, instances: SubKpiInstance[]) {
  const mandatory = mandatoryCatalogByCategory.get(category) || new Set<string>();
  // When no profile is selected (mandatory set is empty), still normalize to
  // clear stale is_mandatory flags from a previously-selected profile.
  if (mandatory.size === 0) return normalizeMandatoryFlags(category, instances || []);

  // Migration: if finance category now mandates kpi_cost_progress_v1 but project
  // only has the old kpi_cost_variance_v1 mandatory instance (from the previous
  // template default), remove the orphaned variance instance so the progress
  // chart replaces it cleanly.
  let migrated = instances || [];
  if (category === "finance" && mandatory.has("kpi_cost_progress_v1")) {
    const hasProgress = migrated.some((i) => String(i.catalog_kpi_guid).toLowerCase() === "kpi_cost_progress_v1");
    if (!hasProgress) {
      migrated = migrated.filter((i) => String(i.catalog_kpi_guid).toLowerCase() !== "kpi_cost_variance_v1");
    }
  }

  const normalized = normalizeMandatoryFlags(category, migrated);
  const existingMandatoryCatalog = new Set(
    (normalized || []).filter((i) => !!i.is_mandatory).map((i) => String(i.catalog_kpi_guid).toLowerCase())
  );
  const out: SubKpiInstance[] = [...(normalized || [])];
  // Add missing mandatory instances at the top (in template order)
  const templateList = Array.from(mandatory);
  templateList.forEach((catalogId) => {
    if (!existingMandatoryCatalog.has(String(catalogId).toLowerCase())) {
      const catItem = kpiCatalog.find((c) => String(c.kpi_catalog_guid).toLowerCase() === String(catalogId).toLowerCase());
      out.unshift({
        instance_guid: makeInstanceGuid(),
        catalog_kpi_guid: String(catalogId),
        display_name: String(catItem?.title || catalogId),
        weight: undefined,
        is_mandatory: true,
        main_chart: (catItem?.allowed_main_charts?.[0] as any) || "bar",
        history_chart: (catItem?.allowed_history_charts?.[0] as any) || "line",
        data_binding_note: "",
      });
    }
  });
  return normalizeMandatoryFlags(category, out);
}

const displayedKpiInstances = useMemo(() => {
  const out: Record<KpiCategoryKey, SubKpiInstance[]> = {
    schedule: ensureMandatoryInstances("schedule", draftKpiInstances.schedule),
    finance: ensureMandatoryInstances("finance", draftKpiInstances.finance),
    mission: ensureMandatoryInstances("mission", draftKpiInstances.mission),
    data_reliability: ensureMandatoryInstances("data_reliability", draftKpiInstances.data_reliability),
  };
  return out;
}, [draftKpiInstances, mandatoryCatalogByCategory, kpiCatalog]);

const editingSubKpi = useMemo(() => {
  const g = (editingSubKpiInstanceGuid || "").trim();
  if (!g) return null;
  return (displayedKpiInstances[addKpiCategory] || []).find((x) => guidEquals(x.instance_guid, g)) || null;
}, [editingSubKpiInstanceGuid, displayedKpiInstances, addKpiCategory]);


  // Custom metadata values (schema-driven)
  const [customValues, setCustomValues] = useState<FieldValueMap>({});
  const [customDirty, setCustomDirty] = useState<boolean>(false);

  // DEV-920 Layer 2 Step 3. Per-hierarchy typeahead state, keyed by
  // hierarchyGuid. Replaces the single rotaryQuery/showRotarySuggestions
  // pair, which only supported one hardcoded picker. Each declared
  // schema_hierarchy gets its own query + suggestion-visibility state.
  // Map (not Record) for stable referential identity across renders.
  const [hierarchyQueries, setHierarchyQueries] = useState<Record<string, string>>({});
  const [hierarchyShowSuggestions, setHierarchyShowSuggestions] = useState<Record<string, boolean>>({});

  // @deprecated DEV-920 Layer 2 Step 3. Single-picker state from the
  // pre-hierarchies era. Kept temporarily so the deprecated rotary helper
  // functions still compile; remove in a follow-up cleanup pass.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rotaryQuery, setRotaryQuery] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showRotarySuggestions, setShowRotarySuggestions] = useState<boolean>(false);

  // -----------------------------
  // Primary Location typeahead
  // -----------------------------
  // Search + label resolution live in src/utils/geoLocationSearch (shared
  // with Account Settings + Community Settings). See locationSuggestions
  // and selectedPrimaryLocationLabel below.

  // Owning community display (for collections eligibility/context)
  const getCommunityPathDisplay = (leafGuid: string): string => {
    const g = normalizeGuid(leafGuid);
    if (!g) return "";
    const rootToLeaf = pathFromLeafGuid(g, communityEntities as any, communityEntityRelationships as any);
    const parts = (rootToLeaf ?? []).map((p: any) => String(p?.name ?? "").trim()).filter(Boolean);
    // Display Leaf → Root for readability
    return pathDisplay(parts.reverse());
  };

  const owningCommunityDisplay = ownerType === "community" && ownerGuid ? getCommunityPathDisplay(ownerGuid) : "";

  // DEV-877: client-side mirror of the server's walk_org_root(community_id)
  // helper. Walks the owning-community ancestor chain and returns the
  // nearest org-root entity (entity_type='organization' or matches 'org_%').
  // Returns null when the owner is a user (no community) OR when no org root
  // exists in the chain. This is a read-only indicator — the authoritative
  // value is server-derived (projects.primary_owner_org_guid).
  const primaryOwnerOrg = useMemo(() => {
    if (ownerType !== "community" || !ownerGuid) return null as { name: string; guid: string } | null;
    const rootToLeaf = pathFromLeafGuid(ownerGuid, communityEntities as any, communityEntityRelationships as any);
    if (!rootToLeaf || rootToLeaf.length === 0) return null;
    for (const e of rootToLeaf) {
      const t = String((e as any)?.entity_type ?? "").toLowerCase();
      if (t === "organization" || t.startsWith("org_")) {
        const g = normalizeGuid((e as any)?.entity_guid);
        const name = String((e as any)?.name ?? "").trim();
        if (g && name) return { name, guid: g };
      }
    }
    return null;
  }, [ownerType, ownerGuid, communityEntities, communityEntityRelationships]);


  // DEV: search + label resolution moved to src/utils/geoLocationSearch so
  // Project Settings, Account Settings, and Community Settings all share
  // the same scoring algorithm and label fallbacks. Project Settings keeps
  // its own CSS-module chrome (token chip + suggestions list) but the
  // ranked results and the resolved label come from the shared helpers.
  const locationSuggestions = useMemo(
    () =>
      searchGeoLocations({
        query: primaryLocationQuery,
        entities: communityEntities as any[],
        relationships: communityEntityRelationships as any[],
      }),
    [primaryLocationQuery, communityEntities, communityEntityRelationships],
  );

  const selectedPrimaryLocationLabel = useMemo(
    () =>
      getGeoLocationLabel({
        guid: primaryLocationGuid,
        entities: communityEntities as any[],
        relationships: communityEntityRelationships as any[],
      }),
    [primaryLocationGuid, communityEntities, communityEntityRelationships],
  );

  // -----------------------------
  // 2FA enrollment status — required by useV2CommunityRoles to honor
  // the requires_2fa gate on capability bundles. Mirrors CommunitiesPage.
  // -----------------------------
  const is2FAEnabled = useMemo(() => {
    if (!currentUserGuid) return false;
    const me = currentUserGuid.toUpperCase();
    const user = (apiSecurityUsers || []).find(
      (u: any) => String(u.user_guid).toUpperCase() === me,
    );
    return !!user?.is_two_factor_enabled;
  }, [apiSecurityUsers, currentUserGuid]);

  // V2 evaluator — returns Map<communityGuid, Set<capability>> with the
  // parent-chain cascade already applied inside the evaluator.
  // See POLICY_PROJECT_OWNERSHIP_TRANSFER.md §3.
  const { capsByCommunity: v2Caps } = useV2CommunityRoles({
    userGuid: currentUserGuid || "",
    is2FAEnabled,
    entities: Array.isArray(communityEntities) ? communityEntities : [],
    relationships: Array.isArray(communityEntityRelationships) ? communityEntityRelationships : [],
  });

  // -----------------------------
  // Owner eligibility (org-only)
  // -----------------------------
  const eligibleOwnerCommunities = useMemo(() => {
    // POLICY_PROJECT_OWNERSHIP_TRANSFER.md §1 — eligible destinations are
    // org communities (entity_type='org_*' or 'organization') on which the
    // initiator holds 'community:admin' OR 'community:create_projects',
    // either directly or by inheritance through the parent chain.
    //
    // Authority sources, in priority order:
    //   (a) is_system_admin flag → access every active org.
    //   (b) V2 grants via useV2CommunityRoles — evaluator already walks
    //       the parent chain, so direct + inherited grants are unified.
    //       This is the post-DEV-1050 source of truth.
    //   (c) V1 community_role_assignments — legacy fallback for the
    //       DEV-876 transition window. Drop after V1 admin UI is retired.
    //
    // Approval-on-transfer (POLICY §2) is enforced at transfer time in
    // Phase 2/3 — not here. This filter only decides what appears in the
    // dropdown.

    // ---- 1. Build the candidate set: every active org community ----
    const allOrgs: { entity_guid: string; name: string }[] = [];
    if (Array.isArray(communityEntities)) {
      for (let i = 0; i < communityEntities.length; i++) {
        const e = communityEntities[i];
        if (!isOrgEntity(e)) continue;
        if (e?.is_active === false) continue;
        const g = normalizeGuid(e?.entity_guid);
        const name = String(e?.name ?? "").trim();
        if (!g || !name) continue;
        allOrgs.push({ entity_guid: g, name });
      }
    }
    allOrgs.sort((a, b) => a.name.localeCompare(b.name));

    // ---- 2. System-admin fast-path ----
    // Per CLAUDE.md ("Permission checks use isUserSystemAdmin() fast-path"),
    // sysadmins skip the per-community role-row check entirely and may
    // transfer ownership to any active org community.
    if (currentUserGuid && isUserSystemAdmin(currentUserGuid as any)) {
      return allOrgs;
    }

    // ---- 3. Direct-role set ----
    const allowedRoles = new Set(["content_creator", "admin", "owner"]);
    const directRoleCommunities = new Set<string>();
    if (Array.isArray(apiCommunityRoles)) {
      for (let i = 0; i < apiCommunityRoles.length; i++) {
        const r = apiCommunityRoles[i];
        const userGuid = safeGuid(r?.user_guid);
        const commGuid = safeGuid(r?.community_entity_guid);
        const role = String(r?.role ?? "").toLowerCase();
        if (!userGuid || !commGuid) continue;
        if (userGuid !== currentUserGuid) continue;
        if (!allowedRoles.has(role)) continue;
        directRoleCommunities.add(commGuid);
      }
    }

    // ---- 4. Ancestor walk for inherited authority ----
    // A user with admin/owner on Rotary International should be able to
    // transfer to every Rotary descendant (Zones, Districts, Clubs). Build
    // a child→parents adjacency map once, then walk upward from each
    // candidate org. Cycle-safe via a per-walk visited set.
    const parentsByChild = new Map<string, string[]>();
    if (Array.isArray(communityEntityRelationships)) {
      for (let i = 0; i < communityEntityRelationships.length; i++) {
        const rel = communityEntityRelationships[i];
        const child = safeGuid(rel?.child_entity_guid);
        const parent = safeGuid(rel?.parent_entity_guid);
        if (!child || !parent) continue;
        const list = parentsByChild.get(child);
        if (list) list.push(parent);
        else parentsByChild.set(child, [parent]);
      }
    }

    const hasInheritedV1Authority = (rootGuid: string): boolean => {
      const seen = new Set<string>();
      const stack: string[] = [rootGuid];
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (seen.has(cur)) continue;
        seen.add(cur);
        if (directRoleCommunities.has(cur)) return true;
        const parents = parentsByChild.get(cur);
        if (parents && parents.length > 0) {
          for (let p = 0; p < parents.length; p++) stack.push(parents[p]);
        }
      }
      return false;
    };

    // ---- 5. V2 grants (parent cascade already applied by the evaluator) ----
    // Per POLICY §1, qualifying caps are 'community:admin' (owner/admin) and
    // 'community:create_projects' (content_creator equivalent). This is the
    // post-DEV-1050 source of truth — every grant made via the Permissions
    // panel lands here. The V1 walk above is the transition fallback only.
    const hasV2Authority = (guid: string): boolean => {
      const caps = v2Caps.get(guid);
      if (!caps) return false;
      return caps.has("community:admin") || caps.has("community:create_projects");
    };

    return allOrgs.filter(
      (e) => hasV2Authority(e.entity_guid) || hasInheritedV1Authority(e.entity_guid),
    );
    // BUGFIX (companion to CommunityDetailPage canOpenSettings): apiCommunityRoles
    // and v2Caps MUST be in the deps array. Both hooks resolve asynchronously;
    // without these deps the memo never recomputes when the data arrives,
    // leaving non-sysadmin admins/owners unable to transfer ownership.
  }, [currentUserGuid, communityEntities, communityEntityRelationships, apiCommunityRoles, v2Caps]);

  // -----------------------------
  // Schema + fields
  // -----------------------------
  const schemaOptions = useMemo(() => {
    const base = Array.isArray(apiCommunityData.schemas) ? apiCommunityData.schemas.slice() : [];
    return base.slice().sort((a: any, b: any) => String(a?.schema_name ?? "").localeCompare(String(b?.schema_name ?? "")));
  }, [apiCommunityData.schemas]);

  // Task #220. "Needs values" badge — count of hierarchy-participating
  // fields with no value for this project on the SELECTED schema.
  const metadataNeedsValuesCount = useMemo(
    () =>
      computeMetadataNeedsValuesCount(
        projectGuid,
        "project",
        metadataSchemaGuid,
        apiCommunityData.schemaFields,
        apiCommunityData.hierarchies as any,
        apiCommunityData.metadataValues,
      ),
    [
      projectGuid,
      metadataSchemaGuid,
      apiCommunityData.schemaFields,
      apiCommunityData.hierarchies,
      apiCommunityData.metadataValues,
    ],
  );

  const fieldsForSchema = useMemo(() => {
    const sg = safeGuid(metadataSchemaGuid);
    if (!sg) return [] as any[];
    return (Array.isArray(apiCommunityData.schemaFields) ? apiCommunityData.schemaFields : []).filter(
      (f: any) => safeGuid(f?.schema_guid) === sg
    );
  }, [metadataSchemaGuid, apiCommunityData.schemaFields]);

  /**
   * @deprecated DEV-920 Layer 2 Step 3. Replaced by hierarchyPickers
   * (presence check is now hierarchyPickers.length > 0). Kept temporarily
   * because the deprecated rotaryIndex/rotarySuggestions/selectedRotaryDisplay/
   * applyRotarySelection helpers below still reference it. Remove in a
   * follow-up cleanup pass once we've confirmed no in-flight branch uses it.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hasRotaryFields = useMemo(() => {
    for (let i = 0; i < fieldsForSchema.length; i++) {
      const k = String(fieldsForSchema[i]?.field_key ?? "");
      if (k === "rotary_club" || k === "rotary_district" || k === "rotary_zone") return true;
    }
    return false;
  }, [fieldsForSchema]);

  const fieldGuidByKey = useMemo(() => {
    const m = new Map<string, string>();
    for (let i = 0; i < fieldsForSchema.length; i++) {
      const f = fieldsForSchema[i];
      const key = String(f?.field_key ?? "").trim();
      const fg = safeGuid(f?.field_guid);
      if (key && fg) m.set(key, fg);
    }
    return m;
  }, [fieldsForSchema]);

  // -----------------------------
  // Custom entity index (Rotary typeahead)
  // -----------------------------
  // DEV-918: derive `customEntities` from API community_entities filtered
  // to org_rotary_*. Preserves the seed-shape contract so downstream code
  // (parent walks, filters, displays) continues to read e.parent_entity_guid,
  // e.entity_type, e.entity_code, e.name as before. The parent_entity_guid
  // is synthesized from communityEntityRelationships since the relational
  // table is the source of truth in the API model (legacy seed had it inline).
  const customEntities = useMemo(() => {
    if (!Array.isArray(communityEntities)) return [] as any[];
    const parentMap = new Map<string, string>();
    if (Array.isArray(communityEntityRelationships)) {
      for (const rel of communityEntityRelationships as any[]) {
        const child = safeGuid(rel?.child_entity_guid);
        const parent = safeGuid(rel?.parent_entity_guid);
        if (child && parent) parentMap.set(child, parent);
      }
    }
    // Dedupe by entity_guid. The API can return the same row twice during a
    // brief seed/transition window if two community_entities rows share a
    // GUID (migration accident or legacy soft-deletes), and React would
    // otherwise warn about duplicate keys in the rendered <option> list.
    const seen = new Set<string>();
    const out: any[] = [];
    for (const e of communityEntities as any[]) {
      const t = String(e?.entity_type ?? "").toLowerCase();
      if (!t.startsWith("org_rotary_")) continue;
      const g = safeGuid(e?.entity_guid);
      if (!g) continue;
      if (seen.has(g)) continue;
      seen.add(g);
      out.push({
        ...e,
        parent_entity_guid: parentMap.get(g) || null,
      });
    }
    return out;
  }, [communityEntities, communityEntityRelationships]);

  const customEntityByGuid = useMemo(() => {
    const m = new Map<string, any>();
    if (Array.isArray(customEntities)) {
      for (let i = 0; i < customEntities.length; i++) {
        const e = customEntities[i];
        const g = safeGuid(e?.entity_guid);
        if (g) m.set(g, e);
      }
    }
    return m;
  }, [customEntities]);

  const getCustomEntityPathDisplay = (leafGuid: string): string => {
    const leaf = customEntityByGuid.get(leafGuid);
    if (!leaf) return "";

    const names: string[] = [];
    let cur: any = leaf;
    const visited: Record<string, true> = {};

    while (cur) {
      const g = safeGuid(cur?.entity_guid);
      if (!g || visited[g]) break;
      visited[g] = true;

      const nm = String(cur?.name ?? "").trim();
      if (nm) {
        // include code on the leaf if present
        if (g === leafGuid) {
          const code = String(cur?.entity_code ?? "").trim();
          if (code) names.push(`${nm} (${code})`);
          else names.push(nm);
        } else {
          names.push(nm);
        }
      }

      const parentGuid = safeGuid(cur?.parent_entity_guid);
      if (!parentGuid) break;
      cur = customEntityByGuid.get(parentGuid);
    }

    return names.join(", ");
  };

  /**
   * @deprecated DEV-920 Layer 2 Step 3. Replaced by hierarchyPickers[].index
   * (per-hierarchy search index, derived from each hierarchy's leaf
   * entity_type rather than a hardcoded "starts with org_rotary_" check).
   * Kept temporarily; remove in cleanup pass.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const rotaryIndex = useMemo(() => {
    const out: { guid: string; display: string; search: string; leafName: string; code: string; type: string }[] = [];
    if (!Array.isArray(customEntities)) return out;

    // Defensive dedupe by canonical guid. customEntities is already deduped
    // upstream, but the suggestion list is the user-visible surface where any
    // duplicate would trigger a React duplicate-key warning, so we belt-and-
    // suspenders here too.
    const seen = new Set<string>();
    for (let i = 0; i < customEntities.length; i++) {
      const e = customEntities[i];
      const t = String(e?.entity_type ?? "").toLowerCase();
      if (!t.startsWith("org_rotary_")) continue;
      const g = safeGuid(e?.entity_guid);
      if (!g) continue;
      if (seen.has(g)) continue;
      seen.add(g);
      const leafName = String(e?.name ?? "").trim();
      const code = String(e?.entity_code ?? "").trim();
      const display = getCustomEntityPathDisplay(g);
      const search = `${leafName} ${code} ${display}`.toLowerCase();
      out.push({ guid: g, display, search, leafName, code, type: t });
    }

    out.sort((a, b) => a.display.localeCompare(b.display));
    return out;
  }, [customEntities, customEntityByGuid]);

  /**
   * @deprecated DEV-920 Layer 2 Step 3. Replaced by computeHierarchySuggestions()
   * which is parameterized over the picker (so each hierarchy gets its
   * own suggestions). Kept temporarily; remove in cleanup pass.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const rotarySuggestions = useMemo(() => {
    const q = String(rotaryQuery ?? "").trim().toLowerCase();
    if (!q) return [] as { guid: string; display: string }[];

    const scored: { guid: string; display: string; score: number }[] = [];
    for (let i = 0; i < rotaryIndex.length; i++) {
      const row = rotaryIndex[i];
      const name = row.leafName.toLowerCase();
      const code = row.code.toLowerCase();
      let score = -1;

      if (code && code === q) score = 110; // exact code
      else if (name.startsWith(q)) score = 100;
      else if (code && code.startsWith(q)) score = 95;
      else if (row.search.startsWith(q)) score = 85;
      else if (row.search.includes(` ${q}`)) score = 70;
      else if (row.search.includes(q)) score = 50;

      if (score < 0) continue;
      const depthBoost = Math.min(20, row.display.split(",").length);
      scored.push({ guid: row.guid, display: row.display, score: score + depthBoost });
    }

    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 12).map((x) => ({ guid: x.guid, display: x.display }));
  }, [rotaryQuery, rotaryIndex]);

  /**
   * @deprecated DEV-920 Layer 2 Step 3. Replaced by
   * computeSelectedHierarchyDisplay() which walks any hierarchy's tier
   * list rather than the 3 hardcoded rotary field keys. Kept temporarily;
   * remove in cleanup pass.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const selectedRotaryDisplay = useMemo(() => {
    const clubFg = fieldGuidByKey.get("rotary_club") || "";
    const districtFg = fieldGuidByKey.get("rotary_district") || "";
    const zoneFg = fieldGuidByKey.get("rotary_zone") || "";

    const clubGuid = safeGuid(customValues?.[clubFg]?.value_guid);
    const districtGuid = safeGuid(customValues?.[districtFg]?.value_guid);
    const zoneGuid = safeGuid(customValues?.[zoneFg]?.value_guid);

    // Lookup-and-push helper. Skips empty results so we never render
    // ", , " when customEntityByGuid hasn't populated yet (modal-open
    // race where customValues lands from localStorage before the API
    // community_entities response).
    const parts: string[] = [];
    const pushIfResolved = (g: string) => {
      if (!g) return;
      const full = getCustomEntityPathDisplay(g);
      if (!full) return;
      const leaf = full.split(",")[0] || full;
      if (leaf) parts.push(leaf);
    };
    pushIfResolved(clubGuid);
    pushIfResolved(districtGuid);
    pushIfResolved(zoneGuid);

    return parts.join(", ");
  }, [customValues, fieldGuidByKey, customEntityByGuid]);

  // -----------------------------
  // DEV-920 Layer 2 Step 3 — generic hierarchy pickers
  // -----------------------------
  // Replaces the single hardcoded zone/district/club picker with one
  // picker per declared schema_hierarchy. Each hierarchy contributes:
  //   - its tier metadata (resolved field GUID → field_key/label/entity_type)
  //   - a search index over leaf entities matching the leaf tier's entity_type
  //   - a presentation mode (cascading | unified_path) controlling JSX render
  //
  // entity_walk hierarchies (Geography) are platform-seeded and read from
  // community_l1/l2/l3 directly; they don't need a metadata-value picker
  // and are filtered out here (valueSource === 'metadata_value' only).

  /** Hierarchies declared on the currently-selected schema, metadata-value variant only. */
  const schemaHierarchies = useMemo(() => {
    if (!metadataSchemaGuid) return [] as any[];
    const list = Array.isArray(apiCommunityData.hierarchies)
      ? apiCommunityData.hierarchies
      : [];
    return list.filter((h: any) => {
      const hsg = String(h?.schemaGuid ?? "").toLowerCase();
      const target = String(metadataSchemaGuid).toLowerCase();
      const valueSource = String(h?.valueSource ?? "metadata_value").toLowerCase();
      return hsg === target && valueSource === "metadata_value";
    });
  }, [metadataSchemaGuid, apiCommunityData.hierarchies]);

  /**
   * Per-hierarchy picker descriptors. Each entry has the resolved tier
   * metadata + a pre-built search index over leaf entities. Memoized so
   * the typeahead doesn't re-index on every keystroke.
   */
  const hierarchyPickers = useMemo(() => {
    return schemaHierarchies.map((h: any) => {
      // Parse levelOrder (root → leaf array of field GUIDs). Normalize
      // each GUID to the canonical uppercase form via safeGuid so all
      // downstream comparisons (against fieldsForSchema entries, against
      // hierarchyTierFieldGuids set, etc.) work regardless of which case
      // the database / API returned. PostgreSQL JSONB typically returns
      // lowercase GUIDs while safeGuid normalizes to uppercase — without
      // this normalization the find() below silently misses on every
      // tier, defaulting label to "Tier" and entityType to "" so the
      // picker renders but indexes zero entities.
      let levelOrder: string[] = [];
      try {
        const parsed = JSON.parse(String(h?.levelOrder ?? "[]"));
        if (Array.isArray(parsed)) {
          levelOrder = parsed.map((g) => safeGuid(String(g)));
        }
      } catch {
        // Malformed JSON shouldn't reach the client (server validates),
        // but if it does, treat as empty so the picker just doesn't render.
      }

      // Resolve each tier's field metadata. Empty fieldKey/entityType
      // means the field was deleted after the hierarchy was declared —
      // we still render the picker, but the missing tier won't cascade.
      const tiers = levelOrder.map((fieldGuid) => {
        const f = fieldsForSchema.find(
          (x: any) => safeGuid(x?.field_guid) === fieldGuid,
        );
        return {
          fieldGuid,
          fieldKey: String(f?.field_key ?? ""),
          label: String(f?.label ?? "Tier"),
          entityType: String(f?.entity_type ?? ""),
        };
      });

      // Build the search index over EVERY tier's entity type, not
      // just the leaf. Users can pick at any tier (e.g., a District
      // directly), and applyHierarchySelection figures out which tier
      // the selected entity belongs to via tierByEntityType lookup.
      // This matches the prior rotary picker UX where typing a club,
      // district, or zone name all surfaced results.
      //
      // Transition-safe matching (DEV-855): server entities use the
      // 'org_<type>' prefix while older schema fields may store the
      // un-prefixed form. Match either shape until the schema seed
      // is fully migrated.
      const acceptedTypes = new Set<string>();
      for (const tier of tiers) {
        if (!tier.entityType) continue;
        acceptedTypes.add(tier.entityType);
        acceptedTypes.add(`org_${tier.entityType}`);
      }

      const index: {
        guid: string;
        display: string;
        search: string;
        leafName: string;
        code: string;
      }[] = [];
      const seen = new Set<string>();
      if (Array.isArray(customEntities) && acceptedTypes.size > 0) {
        for (const e of customEntities as any[]) {
          const t = String(e?.entity_type ?? "");
          if (!acceptedTypes.has(t)) continue;
          const g = safeGuid(e?.entity_guid);
          if (!g || seen.has(g)) continue;
          seen.add(g);
          const leafName = String(e?.name ?? "").trim();
          const code = String(e?.entity_code ?? "").trim();
          const display = getCustomEntityPathDisplay(g);
          const search = `${leafName} ${code} ${display}`.toLowerCase();
          index.push({ guid: g, display, search, leafName, code });
        }
        index.sort((a, b) => a.display.localeCompare(b.display));
      }

      return {
        hierarchyGuid: String(h?.hierarchyGuid ?? ""),
        hierarchyKey: String(h?.hierarchyKey ?? ""),
        displayName: String(h?.displayName ?? h?.hierarchyKey ?? "Hierarchy"),
        presentation: String(h?.presentation ?? "cascading"),
        tiers,
        index,
      };
    });
  }, [schemaHierarchies, fieldsForSchema, customEntities, getCustomEntityPathDisplay]);

  /** Set of every field GUID participating in any hierarchy on this schema. */
  const hierarchyTierFieldGuids = useMemo(() => {
    const set = new Set<string>();
    for (const picker of hierarchyPickers) {
      for (const tier of picker.tiers) {
        if (tier.fieldGuid) set.add(String(tier.fieldGuid).toLowerCase());
      }
    }
    return set;
  }, [hierarchyPickers]);

  /** Compute typeahead suggestions for a given picker and query string. */
  const computeHierarchySuggestions = (
    picker: { index: any[] },
    query: string,
  ): { guid: string; display: string }[] => {
    const q = String(query ?? "").trim().toLowerCase();
    if (!q) return [];

    const scored: { guid: string; display: string; score: number }[] = [];
    for (const row of picker.index) {
      const name = String(row.leafName ?? "").toLowerCase();
      const code = String(row.code ?? "").toLowerCase();
      let score = -1;

      if (code && code === q) score = 110;
      else if (name.startsWith(q)) score = 100;
      else if (code && code.startsWith(q)) score = 95;
      else if (String(row.search ?? "").startsWith(q)) score = 85;
      else if (String(row.search ?? "").includes(` ${q}`)) score = 70;
      else if (String(row.search ?? "").includes(q)) score = 50;

      if (score < 0) continue;
      const depthBoost = Math.min(20, String(row.display ?? "").split(",").length);
      scored.push({ guid: row.guid, display: row.display, score: score + depthBoost });
    }

    scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
    return scored.slice(0, 12).map((x) => ({ guid: x.guid, display: x.display }));
  };

  /**
   * Apply a typeahead selection. Walks UP the entity tree from the
   * selected entity to set every tier's field value. If the user picked
   * a mid-tier entity (e.g., a District directly), tiers BELOW it are
   * cleared; the selected tier and tiers ABOVE it are populated by the
   * ancestor walk.
   */
  const applyHierarchySelection = (
    picker: { tiers: { fieldGuid: string; entityType: string }[] },
    selectedGuid: string,
  ) => {
    // Empty selectedGuid = clear all tier values (Clear button case).
    if (!selectedGuid) {
      for (const tier of picker.tiers) persistCustomValue(tier.fieldGuid, {});
      return;
    }

    // Map entity_type → tierIndex for type-based dispatch. Includes
    // both prefixed and un-prefixed forms (DEV-855 transition-safety).
    const tierByEntityType = new Map<string, number>();
    picker.tiers.forEach((tier, idx) => {
      if (!tier.entityType) return;
      tierByEntityType.set(tier.entityType.toLowerCase(), idx);
      tierByEntityType.set(`org_${tier.entityType}`.toLowerCase(), idx);
    });

    const ent = customEntityByGuid.get(selectedGuid);
    const t = String(ent?.entity_type ?? "").toLowerCase();
    const selectedTierIdx = tierByEntityType.get(t);

    if (selectedTierIdx === undefined) {
      // Unknown entity type — clear all tier values rather than guess.
      for (const tier of picker.tiers) persistCustomValue(tier.fieldGuid, {});
      return;
    }

    // Walk up from the selected entity, collecting one ancestor per
    // remaining tier. ancestors[selectedTierIdx] = selectedGuid;
    // ancestors[selectedTierIdx-1] = its parent; etc.
    const ancestors: (string | null)[] = picker.tiers.map(() => null);
    ancestors[selectedTierIdx] = selectedGuid;
    let cur: any = ent;
    for (let i = selectedTierIdx - 1; i >= 0; i--) {
      const parentGuid = safeGuid(cur?.parent_entity_guid);
      if (!parentGuid) break;
      ancestors[i] = parentGuid;
      cur = customEntityByGuid.get(parentGuid);
      if (!cur) break;
    }

    // Apply: tiers 0..selectedTierIdx get their resolved guid (or
    // cleared if the walk ran out); tiers below get cleared.
    picker.tiers.forEach((tier, idx) => {
      if (idx > selectedTierIdx) {
        persistCustomValue(tier.fieldGuid, {});
      } else {
        const g = ancestors[idx];
        persistCustomValue(tier.fieldGuid, g ? { value_guid: g } : {});
      }
    });
  };

  /**
   * Compute the human-readable selected-path display for a picker.
   * Reads the deepest-set tier's value and runs path-display, mirroring
   * the prior selectedRotaryDisplay behavior generically.
   */
  const computeSelectedHierarchyDisplay = (
    picker: { tiers: { fieldGuid: string }[] },
  ): string => {
    for (let i = picker.tiers.length - 1; i >= 0; i--) {
      const fg = picker.tiers[i].fieldGuid;
      if (!fg) continue;
      const guid = safeGuid(customValues?.[fg]?.value_guid);
      if (guid) return getCustomEntityPathDisplay(guid);
    }
    return "";
  };

  // -----------------------------
  // Load seed values + overrides when opening
  // -----------------------------
  useEffect(() => {
    if (!isOpen) return;

    setActiveTab("basics");
    setProjectName(String(project?.project_name ?? project?.ProjectName ?? "").trim());
    setDescription(String(project?.description ?? project?.Description ?? "").trim());
    setProjectStartDate(String((project as any)?.project_start_date ?? (project as any)?.project_start ?? (project as any)?.start_date ?? "").trim());
    setProjectEndDate(String((project as any)?.project_end_date ?? (project as any)?.project_end ?? (project as any)?.end_date ?? "").trim());
    setScheduleStartDate(String((project as any)?.schedule_start_date ?? (project as any)?.scheduleStartDate ?? "").trim());
    // DEV-576: Budget & Currency
    setBudgetEstimate(String((project as any)?.budget_estimate ?? "").trim());
    setBudgetCurrency(String((project as any)?.budget_currency ?? "USD").trim() || "USD");
    const savedCadenceStr = String((project as any)?.reporting_cadence ?? (project as any)?.reportingCadence ?? "").trim();
    setReportingCadence(savedCadenceStr);

    // Hydrate structured reporting schedule (new model) from saved data
    const savedSchedule = (project as any)?.reporting_schedule;
    if (savedSchedule?.cadence) {
      const byWd = savedSchedule.by_weekday;
      const wdArr: boolean[] = Array.isArray(byWd) && byWd.length === 7
        ? byWd.map(Boolean)
        : Array.isArray(byWd)
          ? [0,1,2,3,4,5,6].map((i) => byWd.includes(i))
          : DEFAULT_CADENCE_VALUE.weekdays;
      setReportingScheduleValue({
        cadence: savedSchedule.cadence,
        interval: savedSchedule.interval ?? 1,
        weekdays: wdArr,
        monthlyMode: savedSchedule.monthly_mode ?? "day",
        monthlyDayOfMonth: savedSchedule.monthly_day_of_month ?? 1,
        monthlyWeekday: savedSchedule.monthly_weekday ?? 1,
      });
    } else if (savedCadenceStr && ["daily","weekly","monthly","quarterly"].includes(savedCadenceStr)) {
      // Migrate from legacy flat cadence string
      setReportingScheduleValue({
        ...DEFAULT_CADENCE_VALUE,
        cadence: savedCadenceStr as CadenceValue["cadence"],
      });
    } else {
      setReportingScheduleValue(DEFAULT_CADENCE_VALUE);
    }
    setGanttColorTheme(String((project as any)?.gantt_color_theme ?? (project as any)?.ganttColorTheme ?? "match_theme").trim());
    // DEV-563: work week
    const rawWw = (project as any)?.work_week ?? (project as any)?.workWeek ?? null;
    setWorkWeek(Array.isArray(rawWw) ? rawWw : null);

    // owner
    const ot = (project?.owner_type as ProjectOwnerType) || (project?.owning_community_entity_guid ? "community" : "user");
    const og = safeGuid(project?.owner_guid ?? project?.owning_community_entity_guid ?? project?.owner_GUID);
    setOwnerType(ot || "user");
    setOwnerGuid(og || currentUserGuid);

    // primary location
    const pl = safeGuid(project?.primary_location_entity_guid ?? project?.primaryLocationEntityGuid);
    setPrimaryLocationGuid(pl);
    // Fold3-style: keep the selected location as a chip, not duplicated in the text field.
    // When a location is already set, leave the query empty so the input shows "Type to change…".
    setPrimaryLocationQuery("");

    // map point (optional)
    setLocationLat(String(project?.location_lat ?? project?.latitude ?? "").trim());
    setLocationLon(String(project?.location_lon ?? project?.longitude ?? "").trim());

    // ── Visibility (DEV-1463) ──
    // Seeds from the real DB column. The fallback is 'team_only' — the CLOSED
    // end of the ladder. The old code fell back to "public", which meant an
    // unrecognised or missing value silently displayed as fully public; failing
    // OPEN on a visibility control is the wrong direction.
    setVisibility((project?.visibility as ProjectVisibility) || "team_only");
    setProgressVisibility((project?.progress_visibility as ProjectVisibility) || "team_only");
    setIsHidden(!!project?.is_hidden);
    setAuditEnabled(!!project?.audit_enabled);

    // DEV-1047 — Removed V1 ACL hydration (onlyMe/draftAcl/permissionsModified).
    // Governance grants are owned by ProjectOwnersAdminsPanel via V2 API.

    // DEV-932: collection links now from useApiCommunityData.
    const projectGuidUpper = String(projectGuid).toUpperCase();
    const seedLinks = (apiCommunityData.collectionProjects || [])
      .filter((cp: any) => String(cp.project_guid).toUpperCase() === projectGuidUpper)
      .map((cp: any) => String(cp.collection_guid));
    setDraftCollections(seedLinks);

    // standard meta
    setProjectCategory(String(project?.project_category ?? "").trim());
    setProjectStatus(String(project?.project_status ?? "").trim());
    setContentStatus(String(project?.content_status ?? "").trim());

    // selections
    setMetadataSchemaGuid(String(project?.metadata_schema_guid ?? "").trim());
    setKpiProfileGuid(String(project?.kpi_profile_guid ?? "").trim());

// KPI config overrides (v1 local prototype)
try {
  const ov = getProjectKpiConfigOverride(projectGuid as any);
  // DEV-814: prefer the project's persisted category_weights when the local
  // override store doesn't have them. Without this, hydrateKpiConfigs (which
  // only carries category_instances) leaves weights blank on every reload —
  // user-saved weights appear to vanish even when the API persisted them.
  const projectWeights = (project as any)?.category_weights;
  if (ov) {
    if (ov.kpi_profile_guid != null) setKpiProfileGuid(String(ov.kpi_profile_guid ?? "").trim());
    if (ov.category_weights) {
      setDraftCategoryWeights({
        ...DEFAULT_CATEGORY_WEIGHTS,
        ...(ov.category_weights as any),
      });
    } else if (projectWeights && typeof projectWeights === 'object') {
      setDraftCategoryWeights({
        ...DEFAULT_CATEGORY_WEIGHTS,
        ...(projectWeights as any),
      });
    } else {
      setDraftCategoryWeights(DEFAULT_CATEGORY_WEIGHTS);
    }
    if (ov.category_instances) {
      const ci: any = ov.category_instances as any;
      // DEV-822 / migration 039 — split HSE rows out of mission before
      // they reach wizard state. Held in a ref for the duration of the
      // wizard session so the save handler can splice them back in.
      const allMission: any[] = Array.isArray(ci.mission) ? ci.mission : [];
      const hseMission = allMission.filter(
        (m: any) =>
          m?.is_system_managed === true ||
          m?.mission_type_key === 'incident_count',
      );
      const userMission = allMission.filter(
        (m: any) =>
          !(m?.is_system_managed === true) &&
          m?.mission_type_key !== 'incident_count',
      );
      heldHseMissionRowsRef.current = hseMission as SubKpiInstance[];
      setDraftKpiInstances({
        schedule: Array.isArray(ci.schedule) ? ci.schedule : [],
        finance: Array.isArray(ci.finance) ? ci.finance : [],
        mission: userMission,
        data_reliability: Array.isArray(ci.data_reliability) ? ci.data_reliability : [],
      });
    } else {
      heldHseMissionRowsRef.current = [];
      setDraftKpiInstances({ schedule: [], finance: [], mission: [], data_reliability: [] });
    }
  } else {
    // No KPI override yet — still try the project's own categoryWeights
    // (DEV-814) before falling back to defaults.
    if (projectWeights && typeof projectWeights === 'object') {
      setDraftCategoryWeights({
        ...DEFAULT_CATEGORY_WEIGHTS,
        ...(projectWeights as any),
      });
    } else {
      setDraftCategoryWeights(DEFAULT_CATEGORY_WEIGHTS);
    }
    heldHseMissionRowsRef.current = [];
    setDraftKpiInstances({ schedule: [], finance: [], mission: [], data_reliability: [] });
  }
} catch {
  // ignore
}


    // DEV-918 followup: custom metadata values now come from API + localStorage
    // only. The static `projectCustomMetadataValues` flatfile previously seeded
    // legacy hand-crafted GUIDs (e.g. 30000000-...) which were silently
    // round-tripped to the batch-upsert endpoint and rejected by schema
    // validation, blocking every save for any project that had a static seed
    // row — even when the user touched a different field. API is the source
    // of truth; localStorage carries unsaved drafts for offline resilience.
    const allOverrides = loadCustomMetaOverrides();
    const patch = allOverrides[projectGuid] || {};
    setCustomValues({ ...patch });
    setCustomDirty(false);

    // ── DEV-819 §3b: API load (server is source of truth when available) ──
    // Layers API values OVER seed + localStorage so the server wins on conflict.
    // Failure is non-fatal — the merged seed/localStorage values are already in
    // state above, so a failed fetch leaves the user with the offline view.
    (async () => {
      try {
        const apiValues = await fetchAllMetadataValues();
        const apiPatch: FieldValueMap = {};
        for (const v of apiValues) {
          if (safeGuid(v.projectGuid) !== projectGuid) continue;
          const fg = safeGuid(v.fieldGuid);
          if (!fg) continue;
          apiPatch[fg] = {
            value_string:  v.valueString  ?? undefined,
            value_number:  v.valueNumber  ?? undefined,
            value_boolean: v.valueBoolean ?? undefined,
            value_date:    v.valueDate    ?? undefined,
            value_enum:    v.valueEnum    ?? undefined,
            value_guid:    v.valueGuid    ?? undefined,
          };
        }
        if (Object.keys(apiPatch).length > 0) {
          setCustomValues((prev) => ({ ...prev, ...apiPatch }));
        }
      } catch (err) {
         
        console.error("[DEV-819] load metadata values from API failed; using seed + localStorage only", err);
      }
    })();

    // DEV-920 Layer 2 Step 3. Reset every per-hierarchy picker's
    // typeahead state on modal open. Replaces the single-picker resets
    // (setRotaryQuery / setShowRotarySuggestions) that lived here when
    // there was only the hardcoded zone/district/club picker.
    setHierarchyQueries({});
    setHierarchyShowSuggestions({});
    setShowLocationSuggestions(false);
    // DEV-946 / DEV-950 (filed): This effect intentionally re-runs ONLY when
    // the modal opens or the project changes — not when the API arrays
    // (apiProjectAcl, apiCommunityData.collectionProjects, project,
    // currentUserGuid) update. Adding those to deps would cause every
    // SignalR-triggered refetch during an editing session to wipe out the
    // user's in-progress drafts. The trade-off is that if the modal is
    // opened BEFORE the API data resolves, the initial drafts are seeded
    // from empty arrays. DEV-950 tracks the proper architectural fix —
    // split this into (a) reset-on-open and (b) hydrate-when-data-arrives-
    // and-user-hasn't-edited-yet — which is too large for the DEV-946
    // sweep scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectGuid]);

  const parseLatLon = (latRaw: string, lonRaw: string): { lat: number; lon: number } | null => {
    const lat = Number(String(latRaw ?? "").trim());
    const lon = Number(String(lonRaw ?? "").trim());
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (lat < -90 || lat > 90) return null;
    if (lon < -180 || lon > 180) return null;
    // Treat 0,0 as "unset" for now
    if (lat === 0 && lon === 0) return null;
    return { lat, lon };
  };

  const setLatLonFromMap = (lat: number, lon: number) => {
    // Leaflet can return longitudes outside ±180° when the map wraps.
    // Normalize to the standard [-180, 180] range.
    const normLon = ((((lon + 180) % 360) + 360) % 360) - 180;
    // Keep editing pleasant: limit to 6 decimals
    const fmt = (v: number) => (Math.round(v * 1_000_000) / 1_000_000).toString();
    setLocationLat(fmt(lat));
    setLocationLon(fmt(normLon));
  };

  const makeMiniMapIcon = () => {
    // Small, unobtrusive pin
    return L.divIcon({
      className: "",
      html:
        '<div style="width:16px;height:16px;border-radius:50%;background:#38bdf8;border:2px solid rgba(255,255,255,0.9);box-shadow:0 6px 14px rgba(0,0,0,0.35);"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  };

  // Initialize the mini map once when the Basics tab is visible.
  useEffect(() => {
    if (!isOpen) return;
    if (activeTab !== "basics") return;
    if (!miniMapContainerRef.current) return;
    if (miniMapRef.current) {
      // Ensure Leaflet calculates size correctly when modal opens.
      setTimeout(() => miniMapRef.current?.invalidateSize(), 0);
      return;
    }

    const existing = parseLatLon(locationLat, locationLon);
    const defaultCenter: [number, number] = existing
      ? [existing.lat, existing.lon]
      : [39.0, -98.0];
    const defaultZoom = existing ? 11 : 4;

    const map = L.map(miniMapContainerRef.current, {
      zoomControl: true,
      attributionControl: false,
    }).setView(defaultCenter, defaultZoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker(defaultCenter, {
      draggable: true,
      icon: makeMiniMapIcon(),
    }).addTo(map);

    marker.on("dragend", () => {
      const ll = marker.getLatLng();
      setLatLonFromMap(ll.lat, ll.lng);
    });

    map.on("click", (e: any) => {
      const ll = e?.latlng;
      if (!ll) return;
      marker.setLatLng(ll);
      setLatLonFromMap(ll.lat, ll.lng);
    });

    miniMapRef.current = map;
    miniMarkerRef.current = marker;

    setTimeout(() => map.invalidateSize(), 0);
  }, [isOpen, activeTab]);

  // If the user switches away from Basics, destroy the mini map.
  // Leaflet binds to a specific DOM element; if we unmount/remount the container, we must recreate the map.
  useEffect(() => {
    if (!isOpen) return;
    if (activeTab === "basics") return;
    if (miniMapRef.current) {
      try {
        miniMapRef.current.remove();
      } catch {
        // ignore
      }
      miniMapRef.current = null;
      miniMarkerRef.current = null;
    }
  }, [activeTab, isOpen]);

  // Keep mini-map marker in sync with typed lat/lon.
  useEffect(() => {
    if (!miniMapRef.current || !miniMarkerRef.current) return;
    if (!isOpen || activeTab !== "basics") return;

    const parsed = parseLatLon(locationLat, locationLon);
    if (!parsed) return;
    const ll = L.latLng(parsed.lat, parsed.lon);
    miniMarkerRef.current.setLatLng(ll);
    // Don't constantly zoom; just pan.
    miniMapRef.current.panTo(ll, { animate: false });
  }, [locationLat, locationLon, isOpen, activeTab]);

  // Cleanup mini map on close
  useEffect(() => {
    if (isOpen) return;
    if (miniMapRef.current) {
      miniMapRef.current.remove();
      miniMapRef.current = null;
      miniMarkerRef.current = null;
    }
  }, [isOpen]);

  // DEV-841: When the parent passes `initialKpiEdit` along with `isOpen=true`,
  // jump straight to the KPIs tab and open the edit form for the requested
  // instance. Used by thermometer pencil buttons (Schedule / Budget / future
  // Mission) so users can go from "look at the chart" to "edit this KPI's
  // settings" in one click instead of: open modal → switch tab → find row → pencil.
  //
  // CRITICAL placement: this useRef + useEffect MUST live BEFORE the
  // `if (!isOpen) return null` guard below, otherwise React's hook order
  // changes between open/closed renders and you get a "rendered more hooks
  // than during the previous render" crash. The effect callback closes
  // over `displayedKpiInstances` and `openEditKpi` which are defined later
  // in this function — that's fine, because the effect body runs AFTER
  // render when those bindings exist.
  const appliedInitialEditRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal closes so reopening with the same target re-fires.
      appliedInitialEditRef.current = null;
      return;
    }
    if (!initialKpiEdit) return;
    const key = `${initialKpiEdit.category}:${initialKpiEdit.instanceGuid}`;
    if (appliedInitialEditRef.current === key) return;
    // Wait until the requested instance is actually present in the loaded
    // instance list (otherwise openEditKpi early-returns and we'd silently
    // open the modal to the wrong tab).
    const instanceList = displayedKpiInstances?.[initialKpiEdit.category] || [];
    const found = instanceList.find((x) => guidEquals(x.instance_guid, initialKpiEdit.instanceGuid));
    if (!found) return;
    appliedInitialEditRef.current = key;
    setActiveTab('kpi');
    // openEditKpi mutates form state; calling synchronously here is safe
    // because we're inside an effect (post-render) and openEditKpi itself
    // only schedules setState calls.
    openEditKpi(initialKpiEdit.category, initialKpiEdit.instanceGuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialKpiEdit, displayedKpiInstances]);

  if (!isOpen) return null;

  const meLabel = `Me (${getUserDisplayName(currentUserGuid, apiSecurityUsers)})`;

  const dispatchProjectUpdated = () => {
    try {
      window.dispatchEvent(new CustomEvent("l4e_project_updated", { detail: { project_guid: projectGuid } }));
    } catch {
      // ignore
    }
  };


  // Draft-only update. We commit to localStorage only when the user clicks Save.
  const persistCustomValue = (field_guid: string, val: FieldValue) => {
    setCustomValues((prev) => ({ ...prev, [field_guid]: val }));
    setCustomDirty(true);
  };

  const commitCustomMetaOverrides = async () => {
    // Store only non-empty FieldValue objects (keeps overrides compact and supports "clear").
    const isEmpty = (fv: any) => {
      if (!fv || typeof fv !== "object") return true;
      const keys = Object.keys(fv);
      if (keys.length === 0) return true;
      for (let i = 0; i < keys.length; i++) {
        const v = fv[keys[i]];
        if (v === null || v === undefined) continue;
        if (typeof v === "string" && v.trim() === "") continue;
        return false;
      }
      return true;
    };

    // ── localStorage path (preserved as transitional fallback during DEV-819 rollout) ──
    const all = loadCustomMetaOverrides();
    const nextPer: Record<string, any> = {};

    for (const [fg, fv] of Object.entries(customValues || {})) {
      if (!fg) continue;
      if (isEmpty(fv)) continue;
      nextPer[String(fg)] = fv;
    }

    if (Object.keys(nextPer).length === 0) {
      delete (all as any)[projectGuid];
    } else {
      (all as any)[projectGuid] = nextPer;
    }

    saveCustomMetaOverrides(all);
    setCustomDirty(false);

    // ── DEV-819 §3b: API persistence via batch upsert ──
    // Builds a request row for every non-empty field that has a known
    // schema_guid. localStorage write above remains as a non-blocking
    // safety net; if the API fails, the user's draft survives the page
    // reload via localStorage and a future retry can sync it forward.
    try {
      const fieldByGuid = new Map<string, any>();
      for (const f of ((apiCommunityData.schemaFields || []) as any[])) {
        const fg = safeGuid((f as any)?.field_guid);
        if (fg) fieldByGuid.set(fg, f);
      }

      const requests: UpsertProjectMetadataValueRequest[] = [];
      for (const [fg, fv] of Object.entries(customValues || {})) {
        if (!fg) continue;
        const field = fieldByGuid.get(fg);
        const schemaGuid = safeGuid(field?.schema_guid);
        if (!schemaGuid) continue;

        const v = fv as FieldValue;
        requests.push({
          projectGuid,
          fieldGuid: fg,
          schemaGuid,
          valueString:  v?.value_string ?? null,
          valueNumber:  v?.value_number ?? null,
          valueBoolean: v?.value_boolean ?? null,
          valueDate:    v?.value_date ?? null,
          valueEnum:    v?.value_enum ?? null,
          valueGuid:    v?.value_guid ?? null,
        });
      }

      if (requests.length > 0) {
        await batchUpsertProjectCustomMetadata(requests);
      }
    } catch (err) {
      // Don't fail the modal save on API errors — the user's data is in
      // localStorage. Log so we can diagnose. A user-facing toast is a
      // follow-up polish item.
       
      console.error("[DEV-819] batch upsert metadata via API failed", err);
    }
  };

  /**
   * @deprecated DEV-920 Layer 2 Step 3. Replaced by applyHierarchySelection()
   * which dispatches on the selected entity_type against any picker's
   * tier definitions, walks ancestors generically, and supports N tiers
   * (was hardcoded to club/district/zone). Kept temporarily; remove in
   * cleanup pass.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const applyRotarySelection = (selectedGuid: string) => {
    const clubFg = fieldGuidByKey.get("rotary_club") || "";
    const districtFg = fieldGuidByKey.get("rotary_district") || "";
    const zoneFg = fieldGuidByKey.get("rotary_zone") || "";

    const ent = customEntityByGuid.get(selectedGuid);
    const t = String(ent?.entity_type ?? "").toLowerCase();

    const setGuid = (fg: string, guid: string | null) => {
      if (!fg) return;
      if (!guid) persistCustomValue(fg, {});
      else persistCustomValue(fg, { value_guid: guid });
    };

    if (t === "org_rotary_club") {
      const district = safeGuid(ent?.parent_entity_guid);
      const districtEnt = district ? customEntityByGuid.get(district) : null;
      const zone = districtEnt ? safeGuid(districtEnt?.parent_entity_guid) : "";
      setGuid(clubFg, selectedGuid);
      setGuid(districtFg, district || null);
      setGuid(zoneFg, zone || null);
    } else if (t === "org_rotary_district") {
      const zone = safeGuid(ent?.parent_entity_guid);
      setGuid(clubFg, null);
      setGuid(districtFg, selectedGuid);
      setGuid(zoneFg, zone || null);
    } else if (t === "org_rotary_zone") {
      setGuid(clubFg, null);
      setGuid(districtFg, null);
      setGuid(zoneFg, selectedGuid);
    } else {
      // unknown rotary type, treat as a clear
      setGuid(clubFg, null);
      setGuid(districtFg, null);
      setGuid(zoneFg, null);
    }
  };



  // Validation helpers (prototype): ensure category KPI weights sum to 100% within each category.
  const subKpiWeightTotalsByCategory: Record<KpiCategoryKey, number> = (() => {
    const cats: KpiCategoryKey[] = ["schedule", "finance", "mission", "data_reliability"];
    const totals: Record<KpiCategoryKey, number> = {
      schedule: 0,
      finance: 0,
      mission: 0,
      data_reliability: 0,
    };

    for (const cat of cats) {
      const items = (displayedKpiInstances as any)?.[cat] || [];
      let total = 0;
      for (const it of items) {
        const w = (it as any)?.weight;
        const n = typeof w === "number" ? w : (String(w ?? "").trim() === "" ? 0 : Number(w));
        if (Number.isFinite(n)) total += n;
      }
      totals[cat] = total;
    }
    return totals;
  })();

  const isSubKpiWeightsOkByCategory: Record<KpiCategoryKey, boolean> = (() => {
    const cats: KpiCategoryKey[] = ["schedule", "finance", "mission", "data_reliability"];
    const ok: Record<KpiCategoryKey, boolean> = {
      schedule: true,
      finance: true,
      mission: true,
      data_reliability: true,
    } as any;

    for (const cat of cats) {
      const items = (displayedKpiInstances as any)?.[cat] || [];
      if (!items.length) {
        ok[cat] = true;
        continue;
      }
      const total = subKpiWeightTotalsByCategory[cat];
      ok[cat] = Math.abs(total - 100) < 0.0001;
    }

    return ok;
  })();

  const categoryWeightsTotal = ((): number => {
    const cats: KpiCategoryKey[] = ["schedule", "finance", "mission", "data_reliability"];
    return cats.reduce((sum, cat) => sum + Number((effectiveCategoryWeights as any)?.[cat] || 0), 0);
  })();

  const isCategoryWeightsTotalOk = categoryWeightsTotal === 100;

  const isAllSubKpiWeightsOk = (["schedule", "finance", "mission", "data_reliability"] as KpiCategoryKey[])
    .every((cat) => isSubKpiWeightsOkByCategory[cat]);
  const handleSave = async () => {
    // Validation: Category weights and SubKPI weights must sum to 100% for consistent scoring.
    if (!kpiTemplate && !isCategoryWeightsTotalOk) {
      window.alert(t("validation.weightsSum", { total: categoryWeightsTotal }));
      return;
    }
    const badCats = (["schedule", "finance", "mission", "data_reliability"] as KpiCategoryKey[])
      .filter((cat) => !isSubKpiWeightsOkByCategory[cat]);
    if (badCats.length) {
      const names = badCats.map((c) => categoryLabels[c]).join(", ");
      window.alert(`SubKPI weights in each category must sum to 100%. Please adjust: ${names}.`);
      return;
    }

    // Validate lat/lon if provided
    const latRaw = String(locationLat ?? "").trim();
    const lonRaw = String(locationLon ?? "").trim();
    if (latRaw) {
      const latNum = Number(latRaw);
      if (!Number.isFinite(latNum) || latNum < -90 || latNum > 90) {
        window.alert(t("validation.latitudeRange"));
        return;
      }
    }
    if (lonRaw) {
      const lonNum = Number(lonRaw);
      if (!Number.isFinite(lonNum) || lonNum < -180 || lonNum > 180) {
        window.alert(t("validation.longitudeRange"));
        return;
      }
    }

    // POLICY_PROJECT_OWNERSHIP_TRANSFER.md Phase 2 — owner-community change
    // routes through the dedicated /transfer-ownership endpoint, which
    // enforces the V2 capability gate and writes a project_transfer_history
    // audit row. The backend now rejects ownership changes via the generic
    // PUT (returns 400 use_transfer_endpoint), so we must intercept here.
    //
    // Detection: compare the dropdown's current ownerGuid to the project's
    // server-side owner (community_id or the legacy owning_community_entity_guid
    // shape). Only the community-tier transfer is in scope here; owner_type==='user'
    // (workshop case) is still a localStorage-only no-op per the existing
    // DEV-913 translation comment.
    const originalOwnerGuid = String(
      (project as any)?.community_id
        ?? (project as any)?.owner_guid
        ?? (project as any)?.owning_community_entity_guid
        ?? (project as any)?.owner_GUID
        ?? ""
    ).toUpperCase();
    const requestedOwnerGuid = String(ownerGuid || "").toUpperCase();
    const ownerCommunityChanged =
      ownerType === "community"
      && requestedOwnerGuid !== ""
      && requestedOwnerGuid !== originalOwnerGuid;

    if (ownerCommunityChanged) {
      try {
        await transferProjectOwnership(String(projectGuid), ownerGuid);
      } catch (e: any) {
        const body = e?.body;
        if (body?.error === "approval_required") {
          window.alert(
            body.message
              ?? "Transfers to this community require approval from a community owner or admin. The approval workflow will be available shortly."
          );
        } else if (body?.error === "forbidden") {
          window.alert(
            body.message
              ?? "You do not have permission to transfer this project to that community."
          );
        } else {
          window.alert(
            e instanceof Error ? e.message : "Ownership transfer failed."
          );
        }
        return; // do NOT proceed with the rest of Save — the user's intent was the transfer
      }
    }

    const patch: Partial<ProjectConfigOverride> = {
      project_name: projectName,
      description,
      project_start_date: String(projectStartDate || "").trim() || undefined,
      project_end_date: String(projectEndDate || "").trim() || undefined,
      // owner_type / owner_guid intentionally OMITTED from the patch when an
      // ownership change happened — the dedicated transfer endpoint already
      // committed the new community on the server. Including them here would
      // round-trip through projectConfigOverrides.ts's DEV-913 translation
      // and hit the now-locked PUT with the same change, causing a 400.
      // Pass them only when no transfer happened, so localStorage stays in
      // sync with the on-screen display.
      ...(ownerCommunityChanged ? {} : {
        owner_type: ownerType,
        owner_guid: ownerGuid || currentUserGuid,
      }),
      primary_location_entity_guid: primaryLocationGuid || undefined,
      location_lat: latRaw || undefined,
      location_lon: lonRaw || undefined,
      // DEV-1463 — the project standard. Both dials go to the API as-is; the
      // server validates them and enforces the cap (progress ≤ visibility),
      // silently auto-downgrading progress when visibility is reduced. The UI
      // below also caps the options, so the auto-downgrade is a backstop rather
      // than a surprise.
      //
      // `public_scope` is retired — folded into the ladder losslessly.
      visibility,
      progress_visibility: progressVisibility,
      is_hidden: isHidden,
      audit_enabled: auditEnabled,
      metadata_schema_guid: metadataSchemaGuid || undefined,
      kpi_profile_guid: kpiProfileGuid || undefined,
      project_category: projectCategory || undefined,
      project_status: projectStatus || undefined,
      content_status: contentStatus || undefined,
      reporting_cadence: (reportingScheduleValue.cadence || reportingCadence || undefined) as ProjectConfigOverride["reporting_cadence"],
      reporting_schedule: {
        cadence: reportingScheduleValue.cadence,
        interval: reportingScheduleValue.interval,
        by_weekday: reportingScheduleValue.weekdays.map((on, idx) => on ? idx : -1).filter((x) => x >= 0),
        monthly_mode: reportingScheduleValue.monthlyMode,
        monthly_day_of_month: reportingScheduleValue.monthlyDayOfMonth,
        monthly_weekday: reportingScheduleValue.monthlyWeekday,
      },
      schedule_start_date: scheduleStartDate || undefined,
      gantt_color_theme: (ganttColorTheme || "match_theme") as ProjectConfigOverride["gantt_color_theme"],
      // DEV-563: Send explicit array (never null) so the API's "is not null" guard passes.
      // null in React state means "all days" → send [1,2,3,4,5,6,7].
      work_week: workWeek ?? [1, 2, 3, 4, 5, 6, 7],
      // DEV-576: Budget & Currency
      budget_estimate: budgetEstimate.trim() || undefined,
      budget_currency: budgetCurrency || "USD",
      // DEV-814: persist category weights through the project Update endpoint.
      // Was previously routed only through upsertProjectKpiConfigOverride,
      // which only fires an API write when category_instances also changes —
      // so weight-only edits were silently dropped.
      // DEV-963: category_weights isn't on Partial<ProjectConfigOverride>;
      // setProjectOverride accepts it via the `as any` cast below. Field is
      // consumed downstream by upsertProjectKpiConfigOverride.
      category_weights: draftCategoryWeights,
    } as any;

    setProjectOverride(projectGuid as any, patch as any);

    // Persist KPI Configuration (project-level overrides).
    // This must run regardless of whether permissions were modified.
    //
    // DEV-822 / migration 039 — splice the held HSE rows back into the
    // mission category before writing the override. The wizard's own
    // state never contained them (split out at load) so they cannot have
    // been re-ordered, re-weighted, or removed by the wizard UI. We re-
    // attach them so downstream consumers (engine, HSE bundle scorer,
    // Mission tab cards) keep seeing them via the local store.
    //
    // Note: the API sync (syncProjectKpiConfigsToApi) independently filters
    // platform-managed rows out of its create/update/reap loops, so this
    // merge is purely for in-process consumers of the override.
    try {
      const persistedKpiInstances = {
        ...displayedKpiInstances,
        mission: [
          ...heldHseMissionRowsRef.current,
          ...(displayedKpiInstances.mission || []),
        ],
      } as typeof displayedKpiInstances;

      upsertProjectKpiConfigOverride({
        project_guid: projectGuid as any,
        kpi_profile_guid: kpiProfileGuid || undefined,
        category_weights: draftCategoryWeights as any,
        category_instances: persistedKpiInstances as any,
      } as any);
    } catch {
      // ignore
    }

    // DEV-932: bulk-replace via API. Server diffs and applies in a single
    // transaction. Eligibility (DEV-877) is enforced server-side; a
    // 403 surfaces to the user via the catch.
    try {
      await replaceProjectCollections(String(projectGuid), {
        collectionGuids: (draftCollections || []).map((g: any) => String(g)),
      });
      refetchSecurity(); // ACL hook also tracks projectAcl; collection links live in community hook
      apiCommunityData.refetch();
    } catch (err: any) {
      window.alert(
        err?.message || "Failed to update project's collection memberships."
      );
    }

    if (customDirty) commitCustomMetaOverrides();

    // DEV-1047 — Removed the V1 ACL save path (~165 lines). It was gated
    // on permissionsModified which could no longer become true after
    // DEV-1040 deleted the JSX setters, so the entire branch was dead.
    // Governance grant lifecycle is now owned by ProjectOwnersAdminsPanel
    // (DEV-1039) which calls PermissionsController directly. The Save
    // handler here no longer touches permissions.
    dispatchProjectUpdated();
    onSaved();
    onClose();
  };

  // NOTE: do not use React hooks here; this component can early-return when closed,
  // and conditional hooks cause compile-time lint errors. The IIFE pattern below
  // is the workaround for derived values that would otherwise be useMemo.

  // Community graph maps for eligibility checks (ancestors/descendants)
  const communityGraph = (() => {
    const parentByChild: Record<string, string> = {};
    const childrenByParent: Record<string, string[]> = {};
    const rels: any[] = Array.isArray(communityEntityRelationships) ? communityEntityRelationships : [];
    for (const r of rels) {
      const parent = normalizeGuid((r as any)?.parent_entity_guid ?? "");
      const child = normalizeGuid((r as any)?.child_entity_guid ?? "");
      if (!parent || !child) continue;
      parentByChild[child] = parent;
      (childrenByParent[parent] ||= []).push(child);
    }
    return { parentByChild, childrenByParent };
  })();


  const normalizeName = (s: string) => String(s || "").trim().toLowerCase();

  const resolveCommunityGuidByName = (name: string): string => {
    const n = normalizeName(name);
    if (!n) return "";
    const ent = (communityEntities as any[])?.find((e) => normalizeName(e?.name) === n);
    return ent ? String(ent.entity_guid) : "";
  };

  const resolveCollectionOwnerCommunityGuid = (c: any): string => {
    // Prefer explicit GUID if present (future DB-backed form)
    const direct = safeGuid(c?.owner_entity_guid ?? c?.owner_community_entity_guid);
    if (direct) return direct;

    const ownerOrg = String(c?.owner_org ?? "").trim();
    if (!ownerOrg) return "";

    // Heuristics for template data ("City of Newport" -> "Newport")
    const cleaned = ownerOrg
      .replace(/^city\s+of\s+/i, "")
      .replace(/^town\s+of\s+/i, "")
      .replace(/^state\s+of\s+/i, "")
      .replace(/^province\s+of\s+/i, "")
      .trim();

    return resolveCommunityGuidByName(cleaned) || resolveCommunityGuidByName(ownerOrg);
  };

  const isCollectionEligibleForProject = (c: any): boolean => {
    // If the project isn't owned by a community, don't restrict eligibility in v1.
    if (ownerType !== "community") return true;

    const projectCommunityGuid = safeGuid(ownerGuid);
    if (!projectCommunityGuid) return true;

    const collectionOwnerGuid = resolveCollectionOwnerCommunityGuid(c);
    if (!collectionOwnerGuid) return true;

    const target = normalizeGuid(collectionOwnerGuid);
    const root = normalizeGuid(projectCommunityGuid);
    if (!target || !root) return true;
    if (target === root) return true;

    // Ancestors: collection owner is a parent of the project's owning community
    let cur = root;
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const p = communityGraph.parentByChild[cur];
      if (!p) break;
      if (p === target) return true;
      cur = p;
    }

    // Descendants: collection owner is inside the project's owning community subtree
    const stack = (communityGraph.childrenByParent[root] || []).slice();
    const visited = new Set<string>();
    while (stack.length) {
      const node = stack.pop() as string;
      if (!node || visited.has(node)) continue;
      visited.add(node);
      if (node === target) return true;
      const kids = communityGraph.childrenByParent[node];
      if (kids && kids.length) stack.push(...kids);
    }

    return false;
  };

  // DEV-1040: V1 upsertAcl / removeAcl helpers deleted. The save path that
  // consumed draftAcl is still in handleSave but inert (gated on
  // permissionsModified, which has no setters after the UI deletion).
  // Full cleanup of the save path + remaining V1 state is DEV-1047.

const setCategoryWeight = (cat: KpiCategoryKey, value: number) => {
  setDraftCategoryWeights((prev) => ({ ...prev, [cat]: value }));
};

const setCategoryInstances = (cat: KpiCategoryKey, next: SubKpiInstance[]) => {
  setDraftKpiInstances((prev) => ({ ...prev, [cat]: next }));
};

const moveKpiInstance = (cat: KpiCategoryKey, fromIndex: number, toIndex: number) => {
  const arr = [...(displayedKpiInstances[cat] || [])];
  if (fromIndex < 0 || fromIndex >= arr.length) return;
  const clampedTo = Math.max(0, Math.min(arr.length - 1, toIndex));
  const [it] = arr.splice(fromIndex, 1);
  arr.splice(clampedTo, 0, it);
  setCategoryInstances(cat, arr);
  setSelectedKpiIndexByCategory((p) => ({ ...p, [cat]: clampedTo }));
};

const removeKpiInstance = (cat: KpiCategoryKey, instanceGuid: string) => {
  const arr = (displayedKpiInstances[cat] || []).filter((i) => {
    if (!guidEquals(i.instance_guid, instanceGuid)) return true;
    // Mandatory instances cannot be removed.
    return !!i.is_mandatory;
  });
  setCategoryInstances(cat, arr);
  setSelectedKpiIndexByCategory((p) => ({ ...p, [cat]: -1 }));
};


const isProgressKpiCatalog = (guid: string): boolean => {
  const g = String(guid || '').trim().toLowerCase();
  return g === 'kpi_sched_progress_v1' || g === 'kpi_cost_progress_v1';
};

const isMissionKpiCatalog = (guid: string): boolean => {
  return String(guid || '').trim().toLowerCase() === 'kpi_mission_outcome_v1';
};
const isFinanceProgressKpiCatalog = (guid: string): boolean => String(guid || '').trim().toLowerCase() === 'kpi_cost_progress_v1';
const openAddKpiForCategory = (cat: KpiCategoryKey) => {
  setAddKpiCategory(cat);
  const first = kpiCatalog
    .filter((c) => c.category === cat || (c.categories || []).includes(cat))
    .filter((c) => !(cat === 'finance' && String(c.kpi_catalog_guid) === 'kpi_cost_variance_v1'))[0]
    || kpiCatalog[0];
  setAddKpiCatalogGuid(String(first?.kpi_catalog_guid || ""));
  setAddKpiDisplayName("");
  // Default: first KPI in the category is full, others are mini.
  const existing = displayedKpiInstances?.[cat] || [];
  setAddKpiDisplayMode(existing.length === 0 ? "full" : "mini");
  setAddKpiMainChart((first?.allowed_main_charts?.[0] as any) || "bar");
  setAddKpiHistoryChart((first?.allowed_history_charts?.[0] as any) || "line");
  setAddKpiBindingNote("");
// Default scoring settings (per instance)
setAddKpiDirection("increase_is_good");
setAddKpiGreenThreshold(80);
setAddKpiYellowThreshold(60);
setAddKpiVarianceYellowPP(5);
setAddKpiVarianceRedPP(10);
setAddKpiScheduleTemplateType("progress");
setAddKpiSpiWarnThreshold(0.95);
setAddKpiSpiCritThreshold(0.90);
setAddKpiSpiMinBaselinePct(5);
setAddKpiSpiWeightPct(50);
setShowAdvancedKpiFields(false);
  setMissionOutcomeValues(defaultMissionOutcomeValues);
  setEditingSubKpiInstanceGuid("");
  setIsAddKpiOpen(true);
};

const openEditKpi = (cat: KpiCategoryKey, instanceGuid: string) => {
  const inst = (displayedKpiInstances[cat] || []).find((x) => guidEquals(x.instance_guid, instanceGuid));
  if (!inst) return;
  setAddKpiCategory(cat);
  setAddKpiCatalogGuid(String(inst.catalog_kpi_guid || ""));
  setAddKpiDisplayName(String(inst.display_name || ""));
  setAddKpiDisplayMode((inst as any)?.display_mode === "mini" ? "mini" : "full");
setAddKpiDirection(((inst as any)?.direction as any) === "decrease_is_good" ? "decrease_is_good" : "increase_is_good");
const g = Number((inst as any)?.green_threshold);
const y = Number((inst as any)?.yellow_threshold);
const vy = Number((inst as any)?.variance_yellow_pp);
const vr = Number((inst as any)?.variance_red_pp);
if (Number.isFinite(vy)) setAddKpiVarianceYellowPP(vy); else setAddKpiVarianceYellowPP(5);
if (Number.isFinite(vr)) setAddKpiVarianceRedPP(vr); else setAddKpiVarianceRedPP(10);
// Fallback to conventional bands if not set
if (Number.isFinite(g)) setAddKpiGreenThreshold(g);
else setAddKpiGreenThreshold(((inst as any)?.direction as any) === "decrease_is_good" ? 20 : 80);
if (Number.isFinite(y)) setAddKpiYellowThreshold(y);
else setAddKpiYellowThreshold(((inst as any)?.direction as any) === "decrease_is_good" ? 40 : 60);
  setAddKpiMainChart((inst.main_chart as any) || "bar");
  setAddKpiHistoryChart((inst.history_chart as any) || "line");
  setAddKpiBindingNote(String(inst.data_binding_note || ""));
  // Read template type from the appropriate field depending on category
  const rawTmpl = String(
    (inst as any)?.schedule_template_type ||
    (inst as any)?.budget_template_type ||
    ""
  );
  setAddKpiScheduleTemplateType(
    rawTmpl === "progress_spi" || rawTmpl === "progress_cpi" ? rawTmpl : "progress"
  );
  const sw = Number((inst as any)?.spi_warning_threshold ?? (inst as any)?.cpi_warning_threshold);
  const sc = Number((inst as any)?.spi_critical_threshold ?? (inst as any)?.cpi_critical_threshold);
  setAddKpiSpiWarnThreshold(Number.isFinite(sw) ? sw : 0.95);
  setAddKpiSpiCritThreshold(Number.isFinite(sc) ? sc : 0.90);
  const smb = Number((inst as any)?.spi_min_baseline_pct ?? (inst as any)?.cpi_min_baseline_pct);
  setAddKpiSpiMinBaselinePct(Number.isFinite(smb) ? smb : 5);
  const swp = Number((inst as any)?.spi_weight_pct ?? (inst as any)?.cpi_weight_pct);
  setAddKpiSpiWeightPct(Number.isFinite(swp) ? swp : 50);
  // Restore mission outcome fields if present
  setMissionOutcomeValues({
    outcome_id: (inst as any).outcome_id || "",
    value_type: (inst as any).value_type || "numeric",
    tracking_mode: (inst as any).tracking_mode || "cumulative",
    direction: (inst as any).direction || "increase_is_good",
    uom: (inst as any).uom || "count",
    cadence: (inst as any).cadence || "monthly",
    target_goal: (inst as any).target_goal ?? undefined,
    target_baseline: (inst as any).target_baseline ?? undefined,
    milestone_total: (inst as any).milestone_total ?? undefined,
    display_name: inst.display_name || "",
    tracking_start_date: (inst as any).tracking_start_date || "",
    deadline_date: (inst as any).deadline_date || "",
    warning_days: (inst as any).warning_days ?? undefined,
    critical_days: (inst as any).critical_days ?? undefined,
  });
  setEditingSubKpiInstanceGuid(String(inst.instance_guid));
  setIsAddKpiOpen(true);
};

// DEV-841 helper moved above the `if (!isOpen) return null` guard at line ~1106
// so its hooks run on every render (Rules of Hooks). See block of hooks just
// before the early return.

const commitAddKpi = () => {
  const catalog = kpiCatalog.find((c) => String(c.kpi_catalog_guid) === String(addKpiCatalogGuid));
  if (!catalog) return;
  const displayName = (addKpiDisplayName || "").trim() || catalog.title;

  const existingArr = [...(displayedKpiInstances[addKpiCategory] || [])];
  const editingGuid = (editingSubKpiInstanceGuid || "").trim();
  const existingIndex = editingGuid ? existingArr.findIndex((x) => guidEquals(x.instance_guid, editingGuid)) : -1;
  const existingInst = existingIndex >= 0 ? existingArr[existingIndex] : null;

  const updated: SubKpiInstance = {
    instance_guid: existingInst?.instance_guid || makeInstanceGuid(),
    catalog_kpi_guid: String(catalog.kpi_catalog_guid),
    display_name: displayName,
    display_mode: addKpiDisplayMode,
    // weight lives on the main KPI Configuration tab (optional)
    weight: existingInst?.weight,
    is_mandatory: existingInst?.is_mandatory,
    main_chart: addKpiMainChart,
    history_chart: addKpiHistoryChart,
    data_binding_note: (addKpiBindingNote || "").trim(),
direction: addKpiDirection,
green_threshold: addKpiGreenThreshold,
yellow_threshold: addKpiYellowThreshold,
  };

  // Progress KPI: store variance thresholds and schedule/budget template type
  if (isProgressKpiCatalog(String(catalog.kpi_catalog_guid))) {
    const _isFin = isFinanceProgressKpiCatalog(String(catalog.kpi_catalog_guid));
    // Finance progress: under budget = good, so direction is always "decrease_is_good"
    if (_isFin) {
      (updated as any).direction = "decrease_is_good";
    }
    (updated as any).variance_yellow_pp = Number(addKpiVarianceYellowPP);
    (updated as any).variance_red_pp = Number(addKpiVarianceRedPP);
    if (_isFin) {
      (updated as any).budget_template_type = addKpiScheduleTemplateType;
      if (addKpiScheduleTemplateType === "progress_cpi") {
        (updated as any).cpi_warning_threshold = Number(addKpiSpiWarnThreshold);
        (updated as any).cpi_critical_threshold = Number(addKpiSpiCritThreshold);
        (updated as any).cpi_min_baseline_pct = Number(addKpiSpiMinBaselinePct);
        (updated as any).cpi_weight_pct = Number(addKpiSpiWeightPct);
      }
    } else {
      (updated as any).schedule_template_type = addKpiScheduleTemplateType;
      if (addKpiScheduleTemplateType === "progress_spi") {
        (updated as any).spi_warning_threshold = Number(addKpiSpiWarnThreshold);
        (updated as any).spi_critical_threshold = Number(addKpiSpiCritThreshold);
        (updated as any).spi_min_baseline_pct = Number(addKpiSpiMinBaselinePct);
        (updated as any).spi_weight_pct = Number(addKpiSpiWeightPct);
      }
    }
  }

  // Mission KPI: store outcome and target configuration
  if (addKpiCategory === "mission") {
    const mv = missionOutcomeValues;
    // Use the mission display name if the user set one via outcome selection
    if (mv.display_name && !((addKpiDisplayName || "").trim())) {
      updated.display_name = mv.display_name;
    }
    updated.outcome_id = mv.outcome_id || undefined;
    updated.value_type = mv.value_type;
    updated.tracking_mode = mv.tracking_mode;
    updated.direction = mv.direction;
    updated.uom = mv.uom;
    updated.cadence = mv.cadence;
    updated.target_goal = mv.target_goal;
    updated.target_baseline = mv.target_baseline;
    updated.milestone_total = mv.milestone_total;
    updated.tracking_start_date = mv.tracking_start_date || undefined;
    updated.deadline_date = mv.deadline_date || undefined;
    updated.warning_days = mv.warning_days;
    updated.critical_days = mv.critical_days;
  }

  if (existingIndex >= 0) {
    // If this is a mandatory KPI, do not allow changing the catalog KPI.
    if (existingInst?.is_mandatory) updated.catalog_kpi_guid = String(existingInst.catalog_kpi_guid);
    existingArr[existingIndex] = updated;
    setCategoryInstances(addKpiCategory, existingArr);
    setSelectedKpiIndexByCategory((p) => ({ ...p, [addKpiCategory]: existingIndex }));
  } else {
    // Auto-weight: 1st KPI → 100; 2nd → both become 50; 3rd+ → 0
    const countBefore = existingArr.length;
    if (countBefore === 0) {
      updated.weight = 100;
    } else if (countBefore === 1) {
      updated.weight = 50;
      existingArr[0] = { ...existingArr[0], weight: 50 };
    } else {
      updated.weight = 0;
    }
    existingArr.push(updated);
    setCategoryInstances(addKpiCategory, existingArr);
    setSelectedKpiIndexByCategory((p) => ({ ...p, [addKpiCategory]: existingArr.length - 1 }));
  }

  setIsAddKpiOpen(false);
};

// Save handler for the new MissionKpiWizard (DEV-814 Phase 3).
// Takes a complete SubKpiInstance from the wizard, applies the same
// add-or-replace + auto-weight logic that commitAddKpi uses, then closes
// the modal. Persistence uses the existing displayedKpiInstances flow.
const commitMissionKpiFromWizard = (instance: SubKpiInstance) => {
  const cat: KpiCategoryKey = "mission";
  const existingArr = [...(displayedKpiInstances[cat] || [])];
  const existingIndex = existingArr.findIndex((x) =>
    guidEquals(x.instance_guid, instance.instance_guid),
  );

  if (existingIndex >= 0) {
    // Edit: preserve mandatory flag and weight from the prior instance.
    const prior = existingArr[existingIndex];
    const merged: SubKpiInstance = {
      ...instance,
      is_mandatory: prior.is_mandatory,
      weight: instance.weight ?? prior.weight,
    };
    if (prior.is_mandatory) {
      // Mandatory KPIs cannot change their catalog item.
      merged.catalog_kpi_guid = String(prior.catalog_kpi_guid);
    }
    existingArr[existingIndex] = merged;
    setCategoryInstances(cat, existingArr);
    setSelectedKpiIndexByCategory((p) => ({ ...p, [cat]: existingIndex }));
  } else {
    // Add: same auto-weight logic as commitAddKpi.
    const countBefore = existingArr.length;
    const newInstance = { ...instance };
    if (countBefore === 0) {
      newInstance.weight = 100;
    } else if (countBefore === 1) {
      newInstance.weight = 50;
      existingArr[0] = { ...existingArr[0], weight: 50 };
    } else {
      newInstance.weight = newInstance.weight ?? 0;
    }
    existingArr.push(newInstance);
    setCategoryInstances(cat, existingArr);
    setSelectedKpiIndexByCategory((p) => ({ ...p, [cat]: existingArr.length - 1 }));
  }

  setIsAddKpiOpen(false);
};

// DEV-840: Save handler for ScheduleKpiWizard. Mirrors commitMissionKpiFromWizard
// but pinned to the schedule category so add-or-replace + auto-weight rules
// match what users already see for Mission.
const commitScheduleKpiFromWizard = (instance: SubKpiInstance) => {
  const cat: KpiCategoryKey = "schedule";
  const existingArr = [...(displayedKpiInstances[cat] || [])];
  const existingIndex = existingArr.findIndex((x) =>
    guidEquals(x.instance_guid, instance.instance_guid),
  );

  if (existingIndex >= 0) {
    const prior = existingArr[existingIndex];
    const merged: SubKpiInstance = {
      ...instance,
      is_mandatory: prior.is_mandatory,
      weight: instance.weight ?? prior.weight,
    };
    if (prior.is_mandatory) {
      // Mandatory KPIs cannot change their catalog item — preserve.
      merged.catalog_kpi_guid = String(prior.catalog_kpi_guid);
    }
    existingArr[existingIndex] = merged;
    setCategoryInstances(cat, existingArr);
    setSelectedKpiIndexByCategory((p) => ({ ...p, [cat]: existingIndex }));
  } else {
    const countBefore = existingArr.length;
    const newInstance = { ...instance };
    if (countBefore === 0) {
      newInstance.weight = 100;
    } else if (countBefore === 1) {
      newInstance.weight = 50;
      existingArr[0] = { ...existingArr[0], weight: 50 };
    } else {
      newInstance.weight = newInstance.weight ?? 0;
    }
    existingArr.push(newInstance);
    setCategoryInstances(cat, existingArr);
    setSelectedKpiIndexByCategory((p) => ({ ...p, [cat]: existingArr.length - 1 }));
  }

  setIsAddKpiOpen(false);
};

const deleteEditingSubKpi = () => {
  const editingGuid = (editingSubKpiInstanceGuid || "").trim();
  if (!editingGuid) return;
  const arr = (displayedKpiInstances[addKpiCategory] || []);
  const inst = arr.find((x) => guidEquals(x.instance_guid, editingGuid));
  if (!inst || inst.is_mandatory) return;
  removeKpiInstance(addKpiCategory, editingGuid);
  setIsAddKpiOpen(false);
};

const isEditingMandatory = !!editingSubKpi?.is_mandatory;

  const toggleCollection = (collection_guid: string) => {
    const cg = String(collection_guid);
    setDraftCollections((prev) => {
      if (prev.includes(cg)) return prev.filter((x) => x !== cg);
      return [...prev, cg];
    });
  };

  return createPortal(
    <div className={styles.backdrop}>
	      <div className={styles.modal}>
        <div className={styles.header}>
	          <div className={styles.title}>{projectName ? t("title", { name: projectName }) : t("titleUnnamed")}</div>
          <CloseButton onClick={onClose} size="md" />
        </div>

        <div className={styles.tabRow}>
          <button
            className={[styles.tabButton, activeTab === "basics" ? styles.tabButtonActive : ""]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setActiveTab("basics")}
          >
            {t("tabs.basics")}
          </button>
          <button
            className={[styles.tabButton, activeTab === "metadata" ? styles.tabButtonActive : ""]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setActiveTab("metadata")}
          >
            {t("tabs.metadata")}
            {/* Task #220. "Needs values" badge — count of hierarchy-
                participating fields with no value on the current schema. */}
            {metadataNeedsValuesCount > 0 ? (
              <span
                aria-label={`${metadataNeedsValuesCount} fields need values`}
                title="Some hierarchy fields have no value yet — set them so this project groups correctly in portfolios."
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minInlineSize: 18,
                  blockSize: 18,
                  marginInlineStart: 6,
                  padding: "0 5px",
                  borderRadius: 9,
                  background: "var(--color-warning)",
                  color: "var(--bg-surface)",
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {metadataNeedsValuesCount}
              </span>
            ) : null}
          </button>
          <button
            className={[styles.tabButton, activeTab === "kpi" ? styles.tabButtonActive : ""]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setActiveTab("kpi")}
          >
            {t("tabs.kpi")}
          </button>
          <button
            className={[styles.tabButton, activeTab === "access" ? styles.tabButtonActive : ""]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setActiveTab("access")}
          >
            {t("tabs.access")}
          </button>
          <button
            className={[styles.tabButton, activeTab === "advanced" ? styles.tabButtonActive : ""]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setActiveTab("advanced")}
          >
            {t("tabs.advanced")}
          </button>
        </div>

        {activeTab === "basics" ? (
  <div className={styles.body}>
    <div className={styles.formRow}>
      <label className={styles.label}>
        Owner{" "}
        <span
          className={styles.infoIcon}
          data-tooltip="Only organizations where you are a Content Creator/Admin/Owner can be selected."
        >
          i
        </span>
      </label>
      <select
        className={styles.select}
        value={ownerType === "user" ? "me" : ownerGuid}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "me") {
            setOwnerType("user");
            setOwnerGuid(currentUserGuid);
            return;
          }
          setOwnerType("community");
          setOwnerGuid(v);
        }}
      >
        <option value="me">{meLabel}</option>
        {eligibleOwnerCommunities.map((c) => (
          <option key={c.entity_guid} value={c.entity_guid}>
            {c.name}
          </option>
        ))}
      </select>

      <div className={styles.inlineRow} style={{ marginTop: 6, alignItems: "center" }}>
        <div className={styles.helpText} style={{ margin: 0 }}>
          Governance
        </div>
        <span
          className={styles.infoIcon}
          data-tooltip="Community owners/admins may administer project permissions. At least one Admin is required."
          style={{ marginInlineStart: 6 }}
        >
          i
        </span>
      </div>

      {/*
        DEV-877: read-only Primary Owner Organization indicator.
        The owning community walks up the org tree to its nearest
        organization-typed ancestor; that ancestor is what the server
        stores as projects.primary_owner_org_guid and what gates
        collection-eligibility (ADR-004 + ADR-005). Surfacing it here
        answers "where does this project belong, structurally?" without
        forcing the user to reason about the org hierarchy themselves.
        Add/edit of explicit partner orgs is deferred to DEV-897
        (template-rostering); this section stays read-only for V1.
      */}
      {ownerType === "community" && ownerGuid ? (
        <div
          className={styles.helpText}
          style={{
            marginTop: 8,
            padding: "8px 10px",
            borderRadius: 4,
            background: "var(--bg-surface, rgba(255,255,255,0.04))",
            border: "1px solid var(--border-default)",
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            Primary owner organization
          </div>
          {primaryOwnerOrg ? (
            <div style={{ color: "var(--text-primary)" }}>{primaryOwnerOrg.name}</div>
          ) : (
            <div style={{ color: "var(--text-muted, rgba(226,232,240,0.55))" }}>
              No organization in this community's ancestor chain. The project will not be eligible for any org-scoped collections until re-parented to an organization.
            </div>
          )}
          <div style={{ color: "var(--text-muted, rgba(226,232,240,0.55))", marginTop: 4 }}>
            Auto-derived from the owning community. Changes when the owner is changed.
          </div>
        </div>
      ) : null}
    </div>

    <div className={styles.formRow}>
      <label className={styles.label}>{t("basics.name")}</label>
      <input className={styles.input} value={projectName} onChange={(e) => setProjectName(e.target.value)} />
    </div>

    <div className={styles.formRow}>
      <label className={styles.label}>{t("basics.description")}</label>
      <textarea
        className={styles.textarea}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
      />
    </div>

<div className={styles.grid2} style={{ marginTop: 6 }}>
  <div className={styles.formRow} style={{ marginBottom: 0 }}>
    <label className={styles.label}>{t("basics.startDate")}</label>
    <input
      className={styles.input}
      type="date"
      value={projectStartDate}
      onChange={(e) => setProjectStartDate(e.target.value)}
    />
  </div>

  <div className={styles.formRow} style={{ marginBottom: 0 }}>
    <label className={styles.label}>{t("basics.endDate")}</label>
    <input
      className={styles.input}
      type="date"
      value={projectEndDate}
      onChange={(e) => setProjectEndDate(e.target.value)}
    />
  </div>
</div>


    <div className={styles.grid2}>
      <div className={styles.formRow} style={{ marginBottom: 0 }}>
        <label className={styles.label}>{t("basics.category")}</label>
        <select className={styles.select} value={projectCategory} onChange={(e) => setProjectCategory(e.target.value)}>
          <option value="">(none)</option>
          {projectCategoryOptions.map((x) => (
            <option key={x} value={x}>
              {localizedProjectCategory(x)}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formRow} style={{ marginBottom: 0 }}>
        <label className={styles.label}>{t("basics.projectStatus")}</label>
        <select className={styles.select} value={projectStatus} onChange={(e) => setProjectStatus(e.target.value)}>
          <option value="">(none)</option>
          {projectStatusOptions.map((x) => (
            <option key={x} value={x}>
              {localizedProjectStatus(x)}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className={styles.grid2}>
      <div className={styles.formRow} style={{ marginBottom: 0 }}>
        <label className={styles.label}>{t("basics.contentStatus")}</label>
        <select className={styles.select} value={contentStatus} onChange={(e) => setContentStatus(e.target.value)}>
          <option value="">(none)</option>
          {contentStatusOptions.map((x) => (
            <option key={x} value={x}>
              {localizedContentStatus(x)}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formRow} style={{ marginBottom: 0 }}>
        <label className={styles.label}>{t("basics.metadataSchema")}</label>
        <select className={styles.select} value={metadataSchemaGuid} onChange={(e) => setMetadataSchemaGuid(e.target.value)}>
          <option value="">(none)</option>
          {schemaOptions.map((s: any) => (
            <option key={s.schema_guid} value={s.schema_guid}>
              {s.schema_name}
            </option>
          ))}
        </select>
        <div className={styles.helpText}>
          Choose a schema to enable schema-driven fields in the "Metadata" tab.
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <label className={styles.label}>{t("basics.primaryLocation")}</label>
      <div className={styles.locationTokenInput}>
        {primaryLocationGuid ? (
          <div className={styles.locationToken} title={selectedPrimaryLocationLabel || ""}>
            <span className={styles.locationTokenText}>{selectedPrimaryLocationLabel}</span>
            <button
              type="button"
              className={styles.locationTokenClear}
              onClick={() => {
                setPrimaryLocationGuid("");
                setPrimaryLocationQuery("");
                setShowLocationSuggestions(false);
              }}
              aria-label="Clear primary location"
            >
              ×
            </button>
          </div>
        ) : null}

        <input
          className={styles.locationTokenField}
          value={primaryLocationQuery}
          placeholder={primaryLocationGuid ? "Type to change…" : "Type a city/state/country…"}
          onChange={(e) => {
            const v = e.target.value;
            // If a token is present and user starts typing, clear the token and treat input as new query.
            if (primaryLocationGuid && v.trim().length > 0) {
              setPrimaryLocationGuid("");
            }
            setPrimaryLocationQuery(v);
            setShowLocationSuggestions(true);
          }}
          onFocus={() => setShowLocationSuggestions(true)}
        />
      </div>
      {showLocationSuggestions && locationSuggestions.length > 0 ? (
        <div className={styles.suggestions}>
          {locationSuggestions.map((s) => (
            <div
              key={s.guid}
              className={styles.suggestionItem}
              onMouseDown={() => {
                setPrimaryLocationGuid(s.guid);
                // Keep the selection in the chip; leave the input empty.
                setPrimaryLocationQuery("");
                setShowLocationSuggestions(false);
              }}
            >
              {s.display}
            </div>
          ))}
        </div>
      ) : null}

      <div className={styles.helpText}>
        When set, Geography grouping is derived from Primary Location (legacy community fields are ignored).
      </div>
    </div>

    <div className={styles.grid2}>
      <div className={styles.formRow} style={{ marginBottom: 0 }}>
        <label className={styles.label}>{t("basics.latitude")}</label>
        <input
          className={styles.input}
          value={locationLat}
          placeholder="e.g. 42.3601"
          inputMode="decimal"
          onChange={(e) => setLocationLat(e.target.value)}
        />
        <div className={styles.helpText}>Optional. Use negative for southern latitudes.</div>
      </div>

      <div className={styles.formRow} style={{ marginBottom: 0 }}>
        <label className={styles.label}>{t("basics.longitude")}</label>
        <input
          className={styles.input}
          value={locationLon}
          placeholder="e.g. -71.0589"
          inputMode="decimal"
          onChange={(e) => setLocationLon(e.target.value)}
        />
        <div className={styles.helpText}>Optional. Use negative for the western hemisphere (U.S. is negative).</div>
      </div>
    </div>

    <div className={styles.formRow}>
      <label className={styles.label}>{t("basics.pinPlacement")}</label>
      <div className={styles.miniMapWrap}>
        <div ref={miniMapContainerRef} className={styles.miniMap} />
      </div>
      <div className={styles.helpText}>Click the map to place the pin or drag it to adjust. This updates Latitude/Longitude.</div>
    </div>

    <div className={styles.helpText}>
      Visibility, roles, and collections are configured in the "Audience & Permissions" tab.
    </div>

    {/* Pre-flight templates are applied via the PreflightChecklist on the Work tab */}
  </div>
) : activeTab === "metadata" ? (
          <div className={styles.body}>
            {!metadataSchemaGuid ? (
              <div className={styles.helpText}>
                Select a Metadata Schema on the Basics tab to configure schema-driven fields.
              </div>
            ) : null}

            {/* DEV-920 Layer 2 Step 3. Render one picker per declared
                schema_hierarchy. presentation === 'unified_path' renders a
                typeahead over leaf entities (one query, walks up to set
                every tier). presentation === 'cascading' renders N selects,
                one per tier, each filtered by its parent's selection.
                Replaces the single hardcoded zone/district/club picker. */}
            {metadataSchemaGuid && hierarchyPickers.length > 0
              ? hierarchyPickers.map((picker) => {
                  if (picker.tiers.length === 0) return null;

                  const query = hierarchyQueries[picker.hierarchyGuid] ?? "";
                  const showSuggestions =
                    hierarchyShowSuggestions[picker.hierarchyGuid] ?? false;
                  const suggestions = computeHierarchySuggestions(picker, query);
                  const selectedDisplay = computeSelectedHierarchyDisplay(picker);

                  // Cascading variant: one <select> per tier. Each select
                  // is filtered by the parent tier's current value (so
                  // picking "District X" narrows the Club select to clubs
                  // whose parent is District X). Suitable when a community
                  // wants explicit per-tier control rather than typeahead.
                  if (picker.presentation === "cascading") {
                    return (
                      <div className={styles.formRow} key={picker.hierarchyGuid}>
                        <label className={styles.label}>{picker.displayName}</label>
                        {picker.tiers.map((tier, tierIdx) => {
                          const parentTier = tierIdx > 0 ? picker.tiers[tierIdx - 1] : null;
                          const parentValueGuid = parentTier
                            ? safeGuid(customValues?.[parentTier.fieldGuid]?.value_guid)
                            : "";
                          const tierValueGuid = safeGuid(customValues?.[tier.fieldGuid]?.value_guid);
                          const prefixedType = tier.entityType ? `org_${tier.entityType}` : "";

                          // Candidate entities for this tier: matching
                          // entity_type AND (root tier OR parent matches).
                          const candidates = (customEntities as any[]).filter((ent: any) => {
                            const et = String(ent?.entity_type ?? "");
                            if (!tier.entityType) return false;
                            if (et !== tier.entityType && et !== prefixedType) return false;
                            if (!parentTier) return true;
                            if (!parentValueGuid) return false;
                            return safeGuid(ent?.parent_entity_guid) === parentValueGuid;
                          });
                          candidates.sort((a: any, b: any) =>
                            String(a?.name ?? "").localeCompare(String(b?.name ?? "")),
                          );

                          return (
                            <select
                              key={tier.fieldGuid}
                              className={styles.select}
                              value={tierValueGuid}
                              disabled={!!parentTier && !parentValueGuid}
                              onChange={(e) => {
                                const v = e.target.value;
                                // Set this tier; clear all tiers below.
                                if (v === "") {
                                  for (let i = tierIdx; i < picker.tiers.length; i++) {
                                    persistCustomValue(picker.tiers[i].fieldGuid, {});
                                  }
                                } else {
                                  persistCustomValue(tier.fieldGuid, { value_guid: v });
                                  for (let i = tierIdx + 1; i < picker.tiers.length; i++) {
                                    persistCustomValue(picker.tiers[i].fieldGuid, {});
                                  }
                                }
                              }}
                            >
                              <option value="">— {tier.label} —</option>
                              {candidates.map((ent: any) => (
                                <option key={ent.entity_guid} value={ent.entity_guid}>
                                  {String(ent?.name ?? ent?.entity_guid)}
                                </option>
                              ))}
                            </select>
                          );
                        })}
                      </div>
                    );
                  }

                  // Unified-path variant (default for hierarchies created
                  // via the Schemas tab editor). Single typeahead; on
                  // selection, applyHierarchySelection walks up to set
                  // every tier. Matches the prior rotary-picker UX, with
                  // the inline-pill chrome from GeoLocationPicker so this
                  // picker reads identically to Account Settings → Home
                  // Community: token lives inside the input wrap, × on
                  // the token itself, "Type to change…" placeholder when
                  // a value is selected, suggestion list anchored below.
                  const placeholderEmpty = `Search ${picker.tiers
                    .map((tier) => String(tier.label || "").toLowerCase())
                    .filter(Boolean)
                    .join(", ")}…`;
                  return (
                    <div className={styles.formRow} key={picker.hierarchyGuid}>
                      <label className={styles.label}>{picker.displayName}</label>
                      <div className={pickerStyles.root}>
                        <div className={pickerStyles.inputWrap}>
                          {selectedDisplay && (
                            <div className={pickerStyles.token} title={selectedDisplay}>
                              <span className={pickerStyles.tokenLabel}>{selectedDisplay}</span>
                              <button
                                type="button"
                                className={pickerStyles.tokenClear}
                                onClick={() => {
                                  applyHierarchySelection(picker, "");
                                  setHierarchyQueries((prev) => ({
                                    ...prev,
                                    [picker.hierarchyGuid]: "",
                                  }));
                                  setHierarchyShowSuggestions((prev) => ({
                                    ...prev,
                                    [picker.hierarchyGuid]: false,
                                  }));
                                }}
                                aria-label="Clear selection"
                              >
                                ×
                              </button>
                            </div>
                          )}
                          <input
                            className={pickerStyles.input}
                            value={query}
                            placeholder={selectedDisplay ? "Type to change…" : placeholderEmpty}
                            onChange={(e) => {
                              const v = e.target.value;
                              // If a token is selected and the user starts
                              // typing, clear it so the input becomes the
                              // active query field. Mirrors GeoLocationPicker.
                              if (selectedDisplay && v.trim().length > 0) {
                                applyHierarchySelection(picker, "");
                              }
                              setHierarchyQueries((prev) => ({
                                ...prev,
                                [picker.hierarchyGuid]: v,
                              }));
                              setHierarchyShowSuggestions((prev) => ({
                                ...prev,
                                [picker.hierarchyGuid]: true,
                              }));
                            }}
                            onFocus={() =>
                              setHierarchyShowSuggestions((prev) => ({
                                ...prev,
                                [picker.hierarchyGuid]: true,
                              }))
                            }
                            // Delay blur so a click on a suggestion
                            // registers before the list hides.
                            onBlur={() =>
                              setTimeout(
                                () =>
                                  setHierarchyShowSuggestions((prev) => ({
                                    ...prev,
                                    [picker.hierarchyGuid]: false,
                                  })),
                                200,
                              )
                            }
                          />
                        </div>
                        {showSuggestions && suggestions.length > 0 ? (
                          <div className={pickerStyles.suggestions} role="listbox">
                            {suggestions.map((s) => (
                              <div
                                key={s.guid}
                                className={pickerStyles.suggestion}
                                role="option"
                                aria-selected="false"
                                // mousedown (not click) so it fires before
                                // the input's blur hides the list.
                                onMouseDown={() => {
                                  applyHierarchySelection(picker, s.guid);
                                  setHierarchyQueries((prev) => ({
                                    ...prev,
                                    [picker.hierarchyGuid]: "",
                                  }));
                                  setHierarchyShowSuggestions((prev) => ({
                                    ...prev,
                                    [picker.hierarchyGuid]: false,
                                  }));
                                }}
                              >
                                {s.display}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div className={styles.helpText}>
                        Search by name or code. Picking a value at any tier
                        sets that tier and walks up to fill its ancestors;
                        tiers below are cleared.
                      </div>
                    </div>
                  );
                })
              : null}

            {metadataSchemaGuid
              ? fieldsForSchema
                  .filter((f: any) => {
                    // DEV-920 Layer 2 Step 3. Hide every field that
                    // participates in a declared hierarchy — those are
                    // rendered by the hierarchy picker above. Was: a
                    // hardcoded check against rotary_club/district/zone
                    // which left the 4th tier (Interact) leaking into the
                    // generic renderer as an orphan dropdown.
                    const fg = String(f?.field_guid ?? "").toLowerCase();
                    if (hierarchyTierFieldGuids.has(fg)) return false;
                    return true;
                  })
                  .map((f: any) => {
                    const fg = safeGuid(f?.field_guid);
                    const type = String(f?.type ?? "string");
                    const label = String(f?.label ?? f?.field_key ?? "Field");
                    const cur = customValues?.[fg] || {};

                    if (!fg) return null;

                    if (type === "string") {
                      return (
                        <div className={styles.formRow} key={fg}>
                          <label className={styles.label}>{label}</label>
                          <input
                            className={styles.input}
                            value={String(cur.value_string ?? "")}
                            onChange={(e) => persistCustomValue(fg, { value_string: e.target.value })}
                          />
                        </div>
                      );
                    }

                    // ── DEV-819 §3a: number / boolean / date / enum renderers ──
                    // Save path remains the existing draft-then-localStorage flow used by
                    // the string handler above. API persistence (batchUpsertProjectCustomMetadata)
                    // is wired in §3b alongside the hierarchical guid picker so all
                    // server-touching changes land together.

                    if (type === "number") {
                      return (
                        <div className={styles.formRow} key={fg}>
                          <label className={styles.label}>{label}</label>
                          <input
                            className={styles.input}
                            type="number"
                            inputMode="decimal"
                            value={cur.value_number != null ? String(cur.value_number) : ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") {
                                persistCustomValue(fg, {});
                                return;
                              }
                              const n = Number(raw);
                              if (Number.isFinite(n)) persistCustomValue(fg, { value_number: n });
                            }}
                          />
                        </div>
                      );
                    }

                    if (type === "boolean") {
                      // TODO(i18n): Yes/No labels need locale keys (projectConfig.metadata.*).
                      // Left hardcoded for now to match the existing pattern in this block —
                      // see line ~2404 fallback "Unsupported field type in prototype" which
                      // also has hardcoded English. Cleanup as a follow-up i18n pass.
                      const v = cur.value_boolean;
                      return (
                        <div className={styles.formRow} key={fg}>
                          <label className={styles.label}>{label}</label>
                          <select
                            className={styles.select}
                            value={v === true ? "true" : v === false ? "false" : ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") persistCustomValue(fg, {});
                              else persistCustomValue(fg, { value_boolean: raw === "true" });
                            }}
                          >
                            <option value="">—</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        </div>
                      );
                    }

                    if (type === "date") {
                      return (
                        <div className={styles.formRow} key={fg}>
                          <label className={styles.label}>{label}</label>
                          <input
                            className={styles.input}
                            type="date"
                            value={String(cur.value_date ?? "")}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") persistCustomValue(fg, {});
                              else persistCustomValue(fg, { value_date: raw });
                            }}
                          />
                        </div>
                      );
                    }

                    if (type === "enum") {
                      // TODO(DEV-819 follow-up): Replace this text input with a
                      // dropdown driven by enum_set_guid lookup once the enum_sets /
                      // enum_set_values tables and API are in place. The schema field
                      // already carries enum_set_guid; the lookup infrastructure is
                      // not in v1 scope.
                      return (
                        <div className={styles.formRow} key={fg}>
                          <label className={styles.label}>{label}</label>
                          <input
                            className={styles.input}
                            value={String(cur.value_enum ?? "")}
                            placeholder="(enum value)"
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") persistCustomValue(fg, {});
                              else persistCustomValue(fg, { value_enum: raw });
                            }}
                          />
                          <div className={styles.helpText}>
                            Enum value list lookup is pending — enter the enum value directly for now.
                          </div>
                        </div>
                      );
                    }

                    if (type === "guid") {
                      // ── DEV-819 §3b: generic single-select guid picker ──
                      // Lists every entity whose entity_type matches the field's
                      // configured entity_type. Hierarchy is shown in the label
                      // ("Zone 33 / District 7930 / Springfield Rotary Club") via
                      // getCustomEntityPathDisplay so the user can disambiguate.
                      //
                      // DEV-920 Layer 2 Step 3 follow-up: fields participating
                      // in a declared schema_hierarchy are now rendered by the
                      // generic hierarchy pickers above (one per declared
                      // hierarchy, driven by hierarchyTierFieldGuids exclusion).
                      // This generic single-select handles the remaining case:
                      // standalone guid fields with no hierarchy declaration
                      // and as a backup for Rotary fields outside the unified flow.
                      //
                      // Transition-safe matching: per DEV-855 collapse, server
                      // entities now use 'org_<type>' prefixed entity_type, while
                      // the schema field still stores the un-prefixed form. Match
                      // both shapes until the schema_fields seed is updated.
                      const fieldEntityType = String(f?.entity_type ?? "");
                      const prefixedType = fieldEntityType ? `org_${fieldEntityType}` : "";
                      const guid = safeGuid(cur.value_guid);
                      const candidates = (customEntities as any[]).filter((ent: any) => {
                        const et = String(ent?.entity_type ?? "");
                        if (!fieldEntityType) return false;
                        return et === fieldEntityType || (prefixedType && et === prefixedType);
                      });
                      candidates.sort((a: any, b: any) => {
                        const an = String(a?.name ?? "");
                        const bn = String(b?.name ?? "");
                        return an.localeCompare(bn);
                      });
                      return (
                        <div className={styles.formRow} key={fg}>
                          <label className={styles.label}>{label}</label>
                          <select
                            className={styles.select}
                            value={guid}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "") persistCustomValue(fg, {});
                              else persistCustomValue(fg, { value_guid: v });
                            }}
                          >
                            <option value="">— Select —</option>
                            {candidates.map((ent: any) => {
                              const eg = safeGuid(ent?.entity_guid);
                              const display = getCustomEntityPathDisplay(eg) || ent?.name || eg;
                              return (
                                <option key={eg} value={eg}>{display}</option>
                              );
                            })}
                          </select>
                          {candidates.length === 0 ? (
                            <div className={styles.helpText}>
                              No entities of type "{fieldEntityType}" are available. Check that the entity hierarchy has been seeded.
                            </div>
                          ) : null}
                        </div>
                      );
                    }

                    return (
                      <div className={styles.formRow} key={fg}>
                        <label className={styles.label}>{label}</label>
                        <div className={styles.helpText}>
                          Unsupported field type in prototype: <code>{type}</code>
                        </div>
                      </div>
                    );
                  })
              : null}
          </div>
        ) : activeTab === "kpi" ? (
          <div className={`${styles.body} ${styles.kpiTab}`}>
            <div className={styles.sectionBox}>
              <div className={styles.sectionTitle}>
                KPI Profile (Optional)
                <span
                  className={styles.infoIcon}
                  data-tooltip="Selecting a KPI Profile locks category weights to the profile template. Sub-KPIs can still be re-weighted and reordered at the project level. If no profile is selected, you can set category weights and then build the Sub-KPI lists from scratch."
                >
                  i
                </span>
              </div>

              <div className={styles.formRow}>
                <select
                  className={styles.select}
                  value={String(kpiProfileGuid || "")}
                  onChange={(e) => {
                    const v = String(e.target.value || "");
                    setKpiProfileGuid(v);
                    // Clear selection indexes when switching profile
                    setSelectedKpiIndexByCategory({
                      schedule: -1,
                      finance: -1,
                      mission: -1,
                      data_reliability: -1,
                    });
                  }}
                >
                  <option value="">(No profile selected)</option>
                  {kpiProfiles.map((p) => (
                    <option key={p.kpi_profile_guid} value={p.kpi_profile_guid}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.sectionBox}>
              <div className={styles.sectionTitle}>
                {t("kpi.weights")}
                <span
                  className={styles.infoIcon}
                  data-tooltip={
                    kpiTemplate
                      ? "Category weights are controlled by the selected KPI Profile (community-owned)."
                      : "Set category weights when no KPI Profile is selected. These must sum to 100% before saving a new profile."
                  }
                >
                  i
                </span>
              </div>

              <div className={styles.kpiWeightsGrid}>
                {(["schedule", "finance", "mission", "data_reliability"] as KpiCategoryKey[]).map((cat) => (
                  <div key={cat} className={styles.kpiWeightRow}>
                    <div className={styles.kpiWeightLabel}><span className={styles.kpiLabelWithIcon}><span className={styles.kpiCategoryIcon}>{categoryIconFor(cat)}</span>{categoryLabels[cat]}</span></div>
                    <input
                      className={styles.kpiWeightInput}
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={effectiveCategoryWeights[cat]}
                      disabled={!!kpiTemplate}
                      onChange={(e) => setCategoryWeight(cat, Number(e.target.value || 0))}
                    />
                    <div className={styles.kpiWeightSuffix}>%</div>
                  </div>
                ))}
              </div>
              {(() => {
                const total = (["schedule", "finance", "mission", "data_reliability"] as KpiCategoryKey[])
                  .reduce((sum, cat) => sum + Number(effectiveCategoryWeights[cat] || 0), 0);
                const isOk = total === 100;
                return (
                  <div className={styles.kpiWeightsTotalRow}>
                    <span className={styles.kpiWeightsTotalLabel}>{t("kpi.total")}</span>
                    <span className={`${styles.kpiWeightsTotalValue} ${isOk ? styles.kpiWeightsTotalOk : styles.kpiWeightsTotalWarn}`}>
                      {total}%
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* KPI Categories (Summary is calculated automatically and not configured here) */}
            {(["schedule", "finance", "mission", "data_reliability"] as KpiCategoryKey[]).map((cat) => {
              const items = displayedKpiInstances[cat] || [];
              const selectedIndex = selectedKpiIndexByCategory[cat] ?? -1;
              const mandatorySet = mandatoryCatalogByCategory.get(cat) || new Set<string>();

              return (
                <div key={cat} className={styles.sectionBox}>
                  <div className={styles.kpiSectionHeader}>
                    <div className={styles.sectionTitle} style={{ marginTop: 0, marginBottom: 0 }}>
                      <span className={styles.kpiLabelWithIcon}><span className={styles.kpiCategoryIcon}>{categoryIconFor(cat)}</span>{categoryLabels[cat]} KPIs</span>
                      <span
                        className={styles.infoIcon}
                        data-tooltip="Add, reorder, and weight Sub-KPIs inside this category. Blank weights mean informational-only (not scored). Mandatory KPIs come from the selected profile template."
                      >
                        i
                      </span>
                    </div>
                    <button className={styles.kpiAddBtn} onClick={() => openAddKpiForCategory(cat)}>
                      + Add
                    </button>
                  </div>

                  <div className={styles.kpiCategoryGrid}>
                    <div className={styles.kpiList}>
                      {items.length > 0 && (
                        <div className={styles.kpiListHeader}>
                          <div />
                          <div>{t("kpi.nameHeader")}</div>
                          <div>{t("kpi.weightHeader")}</div>
                          <div style={{ textAlign: "center" }}>{t("kpi.orderHeader")}</div>
                          <div />
                        </div>
                      )}
                      {items.length === 0 ? (
                        <div className={styles.helpText}>{t("kpi.noKpis")}</div>
                      ) : (
                        items.map((it, idx) => {
                          const isSelected = idx === selectedIndex;
                          const isMandatory = !!it.is_mandatory;

                          // DEV-840: Schedule rows render via ScheduleKpiListRow
                          // (two-line, status dot + freshness). Other categories
                          // continue with the legacy single-line row until they
                          // get their own per-category redesigns.
                          if (cat === "schedule") {
                            return (
                              <ScheduleKpiListRow
                                key={it.instance_guid}
                                instance={it}
                                currentScore={null}
                                lastUpdated={null}
                                cadenceDays={(() => {
                                  // Tiny inline helper — convert cadence string to a length
                                  // in days for the freshness "Updated Nd ago" calculation.
                                  // Kept inline rather than a top-level helper to avoid
                                  // touching the broader ProjectConfigModal exports.
                                  const c = String(it.cadence || 'weekly').toLowerCase();
                                  if (c === 'daily') return 1;
                                  if (c === 'weekly') return 7;
                                  if (c === 'monthly') return 30;
                                  if (c === 'quarterly') return 90;
                                  if (c === 'yearly' || c === 'annual') return 365;
                                  return 7; // sensible default
                                })()}
                                gracePeriod={Number((it as any).permissible_days_late ?? 2)}
                                onEdit={(inst) => openEditKpi("schedule", String(inst.instance_guid))}
                                onRemove={(inst) =>
                                  removeKpiInstance("schedule", String(inst.instance_guid))
                                }
                                onWeightChange={(inst, w) => {
                                  const next = items.map((x) =>
                                    guidEquals(x.instance_guid, inst.instance_guid)
                                      ? { ...x, weight: Number.isFinite(w) ? w : undefined }
                                      : x,
                                  );
                                  setCategoryInstances("schedule", next);
                                }}
                                /* DEV-840 follow-up: reorder arrows preserved
                                   from the legacy row. moveKpiInstance is the
                                   same helper the legacy row used. */
                                onMoveUp={(inst) => {
                                  const i = items.findIndex((x) =>
                                    guidEquals(x.instance_guid, inst.instance_guid));
                                  if (i > 0) moveKpiInstance("schedule", i, i - 1);
                                }}
                                onMoveDown={(inst) => {
                                  const i = items.findIndex((x) =>
                                    guidEquals(x.instance_guid, inst.instance_guid));
                                  if (i >= 0 && i < items.length - 1)
                                    moveKpiInstance("schedule", i, i + 1);
                                }}
                                canMoveUp={idx > 0}
                                canMoveDown={idx < items.length - 1}
                              />
                            );
                          }

                          return (
                            <div
                              key={it.instance_guid}
                              className={`${styles.kpiRow} ${isSelected ? styles.kpiRowSelected : ""}`}
                              onClick={() =>
                                setSelectedKpiIndexByCategory((p) => ({
                                  ...p,
                                  [cat]: idx,
                                }))
                              }
                              title={isMandatory ? "Mandatory KPI (from profile template)" : "Project KPI"}
                            >
                              <button
                                type="button"
                                className={styles.kpiEditIcon}
                                title={isMandatory ? "Edit (mandatory KPI cannot change catalog)" : "Edit"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditKpi(cat, String(it.instance_guid));
                                }}
                              >
                                ✎
                              </button>
                              <div className={styles.kpiRowName}>
                                {it.display_name || "KPI"}
                                {isMandatory ? (
                                  <span
                                    className={styles.kpiTag}
                                    title="From the profile template — cannot be removed."
                                  >
                                    Mandatory
                                  </span>
                                ) : (
                                  <span
                                    className={styles.kpiTag}
                                    style={{
                                      background: "transparent",
                                      border: "1px solid var(--border-default)",
                                      color: "var(--text-tertiary)",
                                    }}
                                    title="Custom KPI added to this project — can be edited or removed."
                                  >
                                    Custom
                                  </span>
                                )}
                              </div>

                              <input
                                className={`${styles.input} ${styles.kpiRowWeight}`}
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={it.weight ?? ""}
                                placeholder="—"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const raw = String(e.target.value ?? "").trim();
                                  const nextWeight = raw === "" ? undefined : Number(raw);
                                  const next = items.map((x) =>
                                    guidEquals(x.instance_guid, it.instance_guid)
                                      ? { ...x, weight: Number.isFinite(nextWeight as any) ? (nextWeight as number) : undefined }
                                      : x
                                  );
                                  setCategoryInstances(cat, next);
                                }}
                              />

                              <div className={styles.kpiReorderCell}>
                                <button
                                  className={styles.kpiMoveBtn}
                                  disabled={idx <= 0}
                                  onClick={(e) => { e.stopPropagation(); moveKpiInstance(cat, idx, idx - 1); }}
                                  title={t("kpi.moveUp")}
                                >
                                  ▲
                                </button>
                                <button
                                  className={styles.kpiMoveBtn}
                                  disabled={idx >= items.length - 1}
                                  onClick={(e) => { e.stopPropagation(); moveKpiInstance(cat, idx, idx + 1); }}
                                  title={t("kpi.moveDown")}
                                >
                                  ▼
                                </button>
                              </div>

                              <button
                                className={styles.kpiRemoveBtn}
                                disabled={isMandatory}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  removeKpiInstance(cat, String(it.instance_guid));
                                }}
                                title={isMandatory ? "Mandatory KPIs cannot be removed." : "Remove KPI"}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })
                      )}

                      {(() => {
                        const total = subKpiWeightTotalsByCategory[cat];
                        const isOk = isSubKpiWeightsOkByCategory[cat];
                        if (!items.length) return null;
                        return (
                          <div className={styles.kpiWeightsTotalRow}>
                            <span className={styles.kpiWeightsTotalLabel}>Total</span>
                            <span
                              className={`${styles.kpiWeightsTotalValue} ${isOk ? styles.kpiWeightsTotalOk : styles.kpiWeightsTotalWarn}`}
                              title={isOk ? "OK" : "SubKPI weights must sum to 100%"}
                            >
                              {Number.isFinite(total) ? `${total}%` : "-"}
                            </span>
                          </div>
                        );
                      })()}
                      {!isSubKpiWeightsOkByCategory[cat] && items.length ? (
                        <div className={styles.helpText} style={{ marginTop: 4, fontSize: 11 }}>
                          Weights must sum to 100%.
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Audit Trail toggle (project-level) */}
            {canEditProject ? (
              <div className={styles.sectionBox}>
                <div className={styles.sectionTitle}>{t("kpi.auditTrail")}</div>
                <label
                  className={styles.formRow}
                  style={{ alignItems: "flex-start", gap: 10, cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={auditEnabled}
                    onChange={(e) => setAuditEnabled(!!e.target.checked)}
                  />
                  <div style={{ display: "grid", gap: 2 }}>
                    <div style={{ fontWeight: 600 }}>{t("kpi.auditEnabled")}</div>
                    <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.35 }}>
                      {t("kpi.auditEnabledDesc")}
                    </div>
                  </div>
                </label>
              </div>
            ) : null}

            {/* Add/Edit SubKPI modal.
                Mission category routes to the new MissionKpiWizard (DEV-814 Phase 3).
                Other categories (schedule, finance, data_reliability) continue to use
                the existing generic form below until they get their own per-category
                redesigns in subsequent passes. */}
            {/* Mission category — slide-in tray (DEV-816 Variant E).
                The tray container is rendered via portal regardless of
                whether `isAddKpiOpen` is set, so unmounting the form
                state stays at the parent. Other categories use the legacy
                centered modal until they get their own per-category
                redesigns. */}
            {isAddKpiOpen && addKpiCategory === "mission" && (
              <Tray
                open={true}
                onClose={() => setIsAddKpiOpen(false)}
                width={560}
              >
                <MissionKpiWizard
                  mode={editingSubKpiInstanceGuid ? "edit" : "add"}
                  existingInstance={editingSubKpi || undefined}
                  projectGuid={projectGuid}
                  onSave={commitMissionKpiFromWizard}
                  onCancel={() => setIsAddKpiOpen(false)}
                />
              </Tray>
            )}
            {/* DEV-840: Schedule wizard tray — same Tray pattern as Mission. */}
            {isAddKpiOpen && addKpiCategory === "schedule" && (
              <Tray
                open={true}
                onClose={() => setIsAddKpiOpen(false)}
                width={560}
              >
                <ScheduleKpiWizard
                  mode={editingSubKpiInstanceGuid ? "edit" : "add"}
                  existingInstance={editingSubKpi || undefined}
                  projectGuid={projectGuid}
                  onSave={commitScheduleKpiFromWizard}
                  onCancel={() => setIsAddKpiOpen(false)}
                />
              </Tray>
            )}
            {isAddKpiOpen && addKpiCategory !== "mission" && addKpiCategory !== "schedule" ? (
              <div className={styles.modalOverlay} onMouseDown={() => setIsAddKpiOpen(false)}>
                <div className={styles.modalCard} onMouseDown={(e) => e.stopPropagation()}>
                  {/* Non-Mission categories continue with the legacy generic form below. */}
                  {false ? null : (
                  <>
                  <div className={styles.modalTitle}>{editingSubKpiInstanceGuid ? t("kpi.editSubKpi") : t("kpi.addSubKpi")}</div>

                  {/* Category — context-aware: shown as read-only label since the
                      modal is always opened from a known category section
                      (openAddKpiForCategory / openEditKpi both set addKpiCategory).
                      Per DEV-814 Phase 3 Quick Win: removed the redundant dropdown
                      that asked users to re-select a category they'd already implied. */}
                  <div className={styles.formRow}>
                    <label className={styles.label}>{t("kpi.category")}</label>
                    <div
                      className={styles.label}
                      style={{
                        padding: "8px 0",
                        fontWeight: 500,
                        color: "var(--text-primary)",
                      }}
                    >
                      {categoryLabels[addKpiCategory]}
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.label}>{t("kpi.kpiType")}</label>
                    <select
                      className={styles.select}
                      value={addKpiCatalogGuid}
                      onChange={(e) => {
                        const v = String(e.target.value);
                        setAddKpiCatalogGuid(v);
                        const item = kpiCatalog.find((c) => String(c.kpi_catalog_guid) === v);
                        if (item) {
                          setAddKpiMainChart((item.allowed_main_charts?.[0] as any) || "bar");
                          setAddKpiHistoryChart((item.allowed_history_charts?.[0] as any) || "line");
                        }
                      }}
                      disabled={isEditingMandatory}
                      title={isEditingMandatory ? t("kpi.mandatoryCannotChange") : t("kpi.selectKpiType")}
                    >
                      {kpiCatalog
                        .filter((c) => c.category === addKpiCategory || (c.categories || []).includes(addKpiCategory))
                        .filter((c) => !(addKpiCategory === 'finance' && String(c.kpi_catalog_guid) === 'kpi_cost_variance_v1'))
                        .map((c) => (
                          <option key={c.kpi_catalog_guid} value={c.kpi_catalog_guid}>
                            {c.title}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.label}>{t("kpi.displayName")}</label>
                    <input
                      className={styles.input}
                      value={addKpiDisplayName}
                      placeholder="e.g., % Complete (Weekly)"
                      onChange={(e) => setAddKpiDisplayName(e.target.value)}
                    />
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.label}>{t("kpi.displayMode")}</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                      <label className={styles.checkboxLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="radio"
                          name="kpi_display_mode"
                          checked={addKpiDisplayMode === "full"}
                          onChange={() => setAddKpiDisplayMode("full")}
                        />
                        Full
                      </label>
                      <label className={styles.checkboxLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          type="radio"
                          name="kpi_display_mode"
                          checked={addKpiDisplayMode === "mini"}
                          onChange={() => setAddKpiDisplayMode("mini")}
                        />
                        Mini
                      </label>
                      <span className={styles.helpText} style={{ marginInlineStart: 6 }}>
                        Full shows the chart; Mini shows a compact summary.
                      </span>
                    </div>
                  </div>

                  {/* Chart type — moved out of Advanced (DEV-814 Quick Win 3).
                      Chart type is fundamental to how the KPI displays, not
                      optional. The block self-renders only when the catalog item
                      offers more than one chart option, so it stays invisible for
                      catalog items with a fixed chart. */}
                  {(() => {
                    const catalogItem = kpiCatalog.find((c) => String(c.kpi_catalog_guid) === String(addKpiCatalogGuid));
                    const mainOpts = catalogItem?.allowed_main_charts || ["bar", "line"];
                    const histOpts = catalogItem?.allowed_history_charts || ["line"];
                    const showMain = mainOpts.length > 1;
                    const showHist = histOpts.length > 1;
                    if (!showMain && !showHist) return null;
                    return (
                      <>
                        {showMain && (
                          <div className={styles.formRow}>
                            <label className={styles.label}>{t("kpi.mainChart")}</label>
                            <select className={styles.select} value={addKpiMainChart} onChange={(e) => setAddKpiMainChart(e.target.value as any)}>
                              {mainOpts.map((o) => (<option key={o} value={o}>{o === "bar" ? "Bar chart" : "Line chart"}</option>))}
                            </select>
                          </div>
                        )}
                        {showHist && (
                          <div className={styles.formRow}>
                            <label className={styles.label}>{t("kpi.historyChart")}</label>
                            <select className={styles.select} value={addKpiHistoryChart} onChange={(e) => setAddKpiHistoryChart(e.target.value as any)}>
                              {histOpts.map((o) => (<option key={o} value={o}>{o === "bar" ? "Bar chart" : "Line chart"}</option>))}
                            </select>
                          </div>
                        )}
                      </>
                    );
                  })()}

{/* DEV-963: Mission Outcome fields block removed here — it was inside the
    `addKpiCategory !== "mission"` branch (line ~3778) so it could never
    render. Mission category has its own MissionKpiWizard tray at line
    ~3747 which renders MissionOutcomeFields via the wizard. */}

{isProgressKpiCatalog(addKpiCatalogGuid) && (() => {
  const _isFinance = isFinanceProgressKpiCatalog(addKpiCatalogGuid);
  const _variantValue = _isFinance ? "progress_cpi" : "progress_spi";
  const _variantLabel = _isFinance ? "Progress + CPI" : "Progress + SPI";
  const _templateLabel = _isFinance ? "Budget Template" : "Schedule Template";
  const _tooltipText = _isFinance
    ? "Progress Only: shows the Budget Progress chart. Progress + CPI: adds a CPI gauge and history chart below the Progress chart for EVM-lite cost performance tracking."
    : "Progress Only: shows the Progress chart. Progress + SPI: adds an SPI gauge and history chart below the Progress chart for EVM-lite schedule performance tracking.";
  return (
<div className={styles.formRow}>
  <label className={styles.label}>
    {_templateLabel}
    <span
      className={styles.infoIcon}
      data-tooltip={_tooltipText}
      style={{ marginInlineStart: 6 }}
    >
      i
    </span>
  </label>
  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
    <label className={styles.checkboxLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        type="radio"
        name="kpi_schedule_template"
        checked={addKpiScheduleTemplateType === "progress"}
        onChange={() => setAddKpiScheduleTemplateType("progress")}
      />
      Progress Only
    </label>
    <label className={styles.checkboxLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        type="radio"
        name="kpi_schedule_template"
        checked={addKpiScheduleTemplateType === _variantValue}
        onChange={() => setAddKpiScheduleTemplateType(_variantValue)}
      />
      {_variantLabel}
    </label>
  </div>

  {/* Direction note for finance */}
  {_isFinance && (
    <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 8, background: "rgba(46, 204, 113, 0.08)", border: "1px solid rgba(46, 204, 113, 0.2)", fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.5 }}>
      <strong style={{ color: "#2ecc71" }}>Cost Direction:</strong> Under budget (actual &lt; baseline) = <span style={{ color: "#2ecc71", fontWeight: 700 }}>Green</span>, Over budget (actual &gt; baseline) = <span style={{ color: "#e74c3c", fontWeight: 700 }}>Red</span>
    </div>
  )}

  {/* Tolerance settings — co-located under Schedule Template */}
  <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 10, border: "1px solid var(--border-subtle)", background: "rgba(0,0,0,0.12)" }}>
    <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.6, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
      Tolerances
    </div>

    {/* Progress variance thresholds (always shown for Progress KPIs) */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span className={styles.helpText} style={{ minWidth: 60, fontWeight: 600 }}>{_isFinance ? "Budget" : "Progress"}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span className={styles.helpText}>{_isFinance ? "Warn" : "Warning"}</span>
        <input className={styles.input} style={{ width: 70 }} type="number" value={String(addKpiVarianceYellowPP)} onChange={(e) => setAddKpiVarianceYellowPP(Number(e.target.value))} />
        <span className={styles.helpText}>{_isFinance ? "% over budget" : "pp"}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span className={styles.helpText}>{_isFinance ? "Critical" : "Critical"}</span>
        <input className={styles.input} style={{ width: 70 }} type="number" value={String(addKpiVarianceRedPP)} onChange={(e) => setAddKpiVarianceRedPP(Number(e.target.value))} />
        <span className={styles.helpText}>{_isFinance ? "% over budget" : "pp"}</span>
      </div>
      {!_isFinance && <span className={styles.helpText} style={{ opacity: 0.65 }}>behind baseline</span>}
    </div>

    {/* SPI/CPI thresholds (only when Progress + SPI/CPI) */}
    {addKpiScheduleTemplateType === _variantValue && (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        <span className={styles.helpText} style={{ minWidth: 60, fontWeight: 600 }}>{_isFinance ? "CPI" : "SPI"}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className={styles.helpText}>Warning</span>
          <input className={styles.input} style={{ width: 70 }} type="number" step={0.01} min={0} max={2} value={String(addKpiSpiWarnThreshold)} onChange={(e) => setAddKpiSpiWarnThreshold(Number(e.target.value))} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className={styles.helpText}>Critical</span>
          <input className={styles.input} style={{ width: 70 }} type="number" step={0.01} min={0} max={2} value={String(addKpiSpiCritThreshold)} onChange={(e) => setAddKpiSpiCritThreshold(Number(e.target.value))} />
        </div>
        <span className={styles.helpText} style={{ opacity: 0.65 }}>{_isFinance ? "ratio (1.0 = on budget)" : "ratio (1.0 = on plan)"}</span>
      </div>
    )}
    {addKpiScheduleTemplateType === _variantValue && (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        <span className={styles.helpText} style={{ minWidth: 60, fontWeight: 600 }}>{_isFinance ? "CPI" : "SPI"}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className={styles.helpText}>Show after baseline reaches</span>
          <input className={styles.input} style={{ width: 60 }} type="number" step={1} min={0} max={50} value={String(addKpiSpiMinBaselinePct)} onChange={(e) => setAddKpiSpiMinBaselinePct(Number(e.target.value))} />
          <span className={styles.helpText}>%</span>
        </div>
        <span className={styles.helpText} style={{ opacity: 0.65 }}>{_isFinance ? "suppresses noisy early-project CPI" : "suppresses noisy early-project SPI"}</span>
      </div>
    )}
    {addKpiScheduleTemplateType === _variantValue && (
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        <span className={styles.helpText} style={{ minWidth: 60, fontWeight: 600 }}>Weight</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className={styles.helpText}>Progress</span>
          <input className={styles.input} style={{ width: 60 }} type="number" step={5} min={0} max={100} value={String(100 - addKpiSpiWeightPct)} onChange={(e) => { const v = Math.max(0, Math.min(100, Number(e.target.value) || 0)); setAddKpiSpiWeightPct(100 - v); }} />
          <span className={styles.helpText}>%</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className={styles.helpText}>{_isFinance ? "CPI" : "SPI"}</span>
          <input className={styles.input} style={{ width: 60 }} type="number" step={5} min={0} max={100} value={String(addKpiSpiWeightPct)} onChange={(e) => { const v = Math.max(0, Math.min(100, Number(e.target.value) || 0)); setAddKpiSpiWeightPct(v); }} />
          <span className={styles.helpText}>%</span>
        </div>
        <span className={styles.helpText} style={{ opacity: 0.65 }}>must total 100% (default 50/50)</span>
      </div>
    )}
  </div>
</div>
  );
})()}

{/* ── Advanced settings (collapsible) ── */}
<div style={{ marginTop: 12 }}>
  <button
    type="button"
    onClick={() => setShowAdvancedKpiFields((v) => !v)}
    style={{
      background: "transparent",
      border: "none",
      color: "var(--text-tertiary)",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 600,
      padding: "4px 0",
      display: "flex",
      alignItems: "center",
      gap: 6,
    }}
  >
    <span style={{ display: "inline-block", transform: showAdvancedKpiFields ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>
      ▸
    </span>
    Advanced Settings
  </button>

  {showAdvancedKpiFields && (
    <div style={{ marginTop: 6, borderInlineStart: "2px solid var(--border-subtle)", paddingInlineStart: 12 }}>

      {/* Scoring Direction — hidden for Progress KPIs (always increase_is_good) and Mission KPIs (direction set per sub-KPI) */}
      {!isProgressKpiCatalog(addKpiCatalogGuid) && !isMissionKpiCatalog(addKpiCatalogGuid) && (
      <div className={styles.formRow}>
        <label className={styles.label}>{t("kpi.scoringDirection")}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <label className={styles.checkboxLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="radio"
              name="kpi_direction"
              checked={addKpiDirection === "increase_is_good"}
              onChange={() => {
                setAddKpiDirection("increase_is_good");
                setAddKpiGreenThreshold(80);
                setAddKpiYellowThreshold(60);
              }}
            />
            Higher is better
          </label>
          <label className={styles.checkboxLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="radio"
              name="kpi_direction"
              checked={addKpiDirection === "decrease_is_good"}
              onChange={() => {
                setAddKpiDirection("decrease_is_good");
                setAddKpiGreenThreshold(20);
                setAddKpiYellowThreshold(40);
              }}
            />
            Lower is better
          </label>
          <span className={styles.helpText} style={{ marginInlineStart: 6 }}>
            Controls how Green/Yellow/Red bands are evaluated.
          </span>
        </div>
      </div>
      )}

      {/* Score Bands — hidden for Progress KPIs (use tolerances) and Mission KPIs (scored per sub-KPI target) */}
      {!isProgressKpiCatalog(addKpiCatalogGuid) && !isMissionKpiCatalog(addKpiCatalogGuid) && (
      <div className={styles.formRow}>
        <label className={styles.label}>{t("kpi.scoreBands")}</label>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className={styles.helpText} style={{ minWidth: 52 }}>Green {addKpiDirection === "decrease_is_good" ? "≤" : "≥"}</span>
            <input className={styles.input} style={{ width: 90 }} type="number" value={String(addKpiGreenThreshold)} onChange={(e) => setAddKpiGreenThreshold(Number(e.target.value))} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className={styles.helpText} style={{ minWidth: 52 }}>Yellow {addKpiDirection === "decrease_is_good" ? "≤" : "≥"}</span>
            <input className={styles.input} style={{ width: 90 }} type="number" value={String(addKpiYellowThreshold)} onChange={(e) => setAddKpiYellowThreshold(Number(e.target.value))} />
          </div>
          <span className={styles.helpText} style={{ marginInlineStart: 6 }}>Red is anything worse than Yellow.</span>
        </div>
      </div>
      )}

      {/* Chart type fields moved out of Advanced — they're fundamental, not
          optional. See the new placement in the main form area, after Display Mode.
          (DEV-814 Phase 3 Quick Win 3.) */}

      <div className={styles.formRow}>
        <label className={styles.label}>{t("kpi.dataBinding")}</label>
        <textarea
          className={styles.textarea}
          rows={2}
          value={addKpiBindingNote}
          placeholder="Example: scheduleProgress.ts → project_guid + reporting_date → baseline/actual/forecast"
          onChange={(e) => setAddKpiBindingNote(e.target.value)}
        />
      </div>
    </div>
  )}
</div>

                  <div className={styles.modalFooter}>
                    {editingSubKpiInstanceGuid && !isEditingMandatory ? (
                      <button className={styles.dangerButton} onClick={deleteEditingSubKpi}>
                        Delete SubKPI
                      </button>
                    ) : null}
                    <button className={styles.secondaryButton} onClick={() => setIsAddKpiOpen(false)}>
                      Cancel
                    </button>
                    <button className={styles.primaryButton} onClick={commitAddKpi}>
                      {editingSubKpiInstanceGuid ? "Save" : "Add"}
                    </button>
                  </div>
                  </>
                  )}
                </div>
              </div>
            ) : null}

            {/* KPI v2 Phase 1: user-entered schedule progress points routed through workflow.
                Commented out — this is an operational concern, not a configuration concern.
                The data collection workflow (cadence-based assignment → user inbox → approval → post)
                will be built as a separate feature. See KpiProgressPanel.tsx for reference code.
            */}
            {/* <KpiProgressPanel
              projectGuid={String(projectGuid)}
              currentUserGuid={String(currentUserGuid)}
              disabled={!canEditProject}
            /> */}
          </div>
        ) : activeTab === "access" ? (
	          <div className={styles.body}>
            {/* DEV-1039 iter 3. Access tab IA split — three subtabs.
                Visibility & Admins is the default because ~90% of admin
                work happens there. Collections is periodic; Permission
                Sets (bundle authoring) is power-user only. Visual mirrors
                the modal's primary .tabButton at a smaller scale so the
                subtab row stays subordinate to the top-level tab bar. */}
            <div
              className={styles.subTabRow}
              role="tablist"
              aria-label={t("access.subtabs.ariaLabel")}
            >
              <button
                type="button"
                role="tab"
                aria-selected={accessSubTab === "visibility"}
                className={[
                  styles.subTabButton,
                  accessSubTab === "visibility" ? styles.subTabButtonActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setAccessSubTab("visibility")}
              >
                {t("access.subtabs.visibility")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={accessSubTab === "collections"}
                className={[
                  styles.subTabButton,
                  accessSubTab === "collections" ? styles.subTabButtonActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setAccessSubTab("collections")}
              >
                {t("access.subtabs.collections")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={accessSubTab === "bundles"}
                className={[
                  styles.subTabButton,
                  accessSubTab === "bundles" ? styles.subTabButtonActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setAccessSubTab("bundles")}
              >
                {t("access.subtabs.bundles")}
              </button>
            </div>

            {accessSubTab === "visibility" && (
              <>
            {/* DEV-1463 — Visibility, rebuilt on THE PROJECT STANDARD.
                (Skip, 2026-07-13: "the mini project approach is what I want to
                implement on full projects so we have a project standard.")

                Row 1: the ladder — who can see the project at all.
                Row 2: Progress — an independent dial, CAPPED at the ladder.
                Row 3: Lockdown — overrides everything (kill switch).

                Replaces DEV-1039's Private/Public + scope-dropdown pair, which
                encoded the same ladder across two fields — and never persisted
                (the value was session-only and silently reset to "Public" on
                every reload, because no DB column existed to hydrate it from).

                Field name `is_hidden` stays in code/DB; only the UI label changed. */}
            <div className={styles.sectionBox}>
              <div className={styles.sectionTitle}>{t("access.visibility")}</div>

              <div className={styles.formRow}>
                <div className={styles.inlineRow}>
                  {(["team_only", "community", "public"] as ProjectVisibility[]).map((v) => (
                    <label key={v} className={styles.checkboxLabel}>
                      <input
                        type="radio"
                        name="visibility"
                        value={v}
                        checked={visibility === v}
                        onChange={() => {
                          setVisibility(v);
                          // Enforce the cap in the UI so the server's silent
                          // auto-downgrade is never a surprise: pulling the
                          // project back also pulls its progress back.
                          if (
                            PROJECT_VISIBILITY_ORDINAL[progressVisibility] >
                            PROJECT_VISIBILITY_ORDINAL[v]
                          ) {
                            setProgressVisibility(v);
                          }
                        }}
                        disabled={isHidden}
                      />
                      {t(`access.visibilityOption.${v}`)}
                    </label>
                  ))}
                </div>
                <div className={styles.helpText}>
                  {t(`access.visibilityHelp.${visibility}`)}
                </div>
              </div>

              {/* Progress — independent, capped at the ladder above. Mirrors
                  mini-projects: progress is the team's working state unless the
                  owner deliberately opens it up as a transparency signal. */}
              <div className={styles.formRow}>
                <div className={styles.inlineRow}>
                  <label className={styles.checkboxLabel} htmlFor="progressVisibility">
                    {t("access.progressVisibility")}
                  </label>
                  <select
                    id="progressVisibility"
                    className={styles.select}
                    value={progressVisibility}
                    onChange={(e) =>
                      setProgressVisibility(e.target.value as ProjectVisibility)
                    }
                    disabled={isHidden}
                  >
                    {(["team_only", "community", "public"] as ProjectVisibility[])
                      // Cannot exceed the project's own visibility — a project
                      // nobody can see cannot have publicly-visible progress.
                      .filter(
                        (v) =>
                          PROJECT_VISIBILITY_ORDINAL[v] <=
                          PROJECT_VISIBILITY_ORDINAL[visibility],
                      )
                      .map((v) => (
                        <option key={v} value={v}>
                          {t(`access.visibilityOption.${v}`)}
                        </option>
                      ))}
                  </select>
                </div>
                <div className={styles.helpText}>{t("access.progressVisibilityHelp")}</div>
              </div>

              {/* Lockdown — overrides visibility. Red-tinted when on. */}
              <div
                className={styles.formRow}
                style={{
                  borderBlockStart: "1px solid var(--border-subtle)",
                  paddingBlockStart: 12,
                  marginBlockStart: 12,
                  ...(isHidden
                    ? {
                        background: "rgba(239, 68, 68, 0.06)",
                        borderRadius: 6,
                        padding: 10,
                      }
                    : {}),
                }}
              >
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isHidden}
                    onChange={(e) => setIsHidden(e.target.checked)}
                  />
                  <strong
                    style={
                      isHidden ? { color: "var(--color-error)" } : undefined
                    }
                  >
                    {t("access.lockdown")}
                  </strong>
                </label>
                <div className={styles.helpText}>{t("access.lockdownHelp")}</div>
              </div>
            </div>

            {/* DEV-1039. Admins — governance grants (Admin / Observer only
                after migration 158 reclassified Contributor + Auditor as
                slot-attached operational bundles). Owner is the singleton
                and is set via Basics. Replaces the V1 ACL editor below
                (V1 retired in DEV-1040). Bundle authoring +
                ProjectPermissionsV2Panel retire in DEV-1038 + DEV-1041.

                DEV-1039 iteration. communityNameByGuid lets the panel resolve
                a selected user's primaryLocationEntityGuid into a readable
                "Home Community" label inside the identity preview card.

                DEV-1039 iter 3. projectOwner surfaces the project owner as a
                read-only context line at the top of the section. Owner is
                managed via the Basics tab — this panel only edits admin
                grants. The line keeps the full picture visible without
                blurring the data model. */}
            <ProjectOwnersAdminsPanel
              projectGuid={String(projectGuid)}
              projectName={projectName}
              canAdmin={canEditProject}
              currentUserGuid={String(currentUserGuid)}
              communityNameByGuid={communityNameByGuid}
              projectOwner={(() => {
                if (!ownerGuid) return undefined;
                if (ownerType === "community") {
                  const name = communityNameByGuid.get(normalizeGuid(ownerGuid)) || "—";
                  return { name, kind: "community" as const };
                }
                // user owner — resolve via apiSecurityUsers
                const u = (apiSecurityUsers || []).find(
                  (su: any) => normalizeGuid(su?.userGuid) === normalizeGuid(ownerGuid),
                );
                const name = u?.displayName || u?.name || "—";
                return { name, kind: "user" as const };
              })()}
            />

            {/* DEV-1040. V1 ACL editor deleted. Governance grants now live in
                ProjectOwnersAdminsPanel above. Inert V1 save path in handleSave
                + related state (onlyMe, draftAcl, permissionsModified) is dead
                code, cleaned up in DEV-1047. */}
              </>
            )}

            {accessSubTab === "collections" && (
              <>
<div className={styles.sectionBox}>
<div className={styles.sectionTitle}>
	              {t("access.collections")}
	              <span
	                className={styles.infoIcon}
	                data-tooltip="Collections organize projects within communities. In v1, collections do not grant additional access to a project. Eligibility is based on the project's owning community and its parent/child communities."
	              >
	                i
	              </span>
	            </div>
            <div className={styles.formRow}>

              <div className={styles.collectionList}>
                {((apiCommunityData.collections || []) as any[]).filter((c: any) => !!c?.is_active).filter((c: any) => isCollectionEligibleForProject(c)).map((c: any) => {
                  const cg = String(c.collection_guid);
                  const checked = draftCollections.includes(cg);
                  return (
                    <label key={cg} className={styles.collectionItem}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCollection(cg)}
                      />
                      <span className={styles.collectionName}>{c.collection_name}</span>
                      <span className={styles.collectionMeta}>{String(c.owner_org ?? "").trim()}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
              </>
            )}

            {accessSubTab === "bundles" && (
              <>
          {/* DEV-1038. Permission Sets — bundle authoring with create/edit/clone/view
              + capability checklist tray. Replaces ProjectPermissionsV2Panel
              (DEV-1041 retires the old mount; the component file can be deleted
              in a follow-up cleanup once no other consumers remain). */}
          <ProjectBundleAuthoringPanel
            projectGuid={String(projectGuid)}
            projectName={projectName}
            canAdmin={canEditProject}
          />
              </>
            )}
          </div>
        ) : activeTab === "advanced" ? (
          <div className={styles.body}>
            {/* ── Reporting Cadence ── */}
            <div className={styles.sectionBox}>
              <div className={styles.sectionTitle}>{t("advanced.sectionReportingCadence")}</div>
              <div className={styles.formRow}>
                <ReportingCadenceWidget
                  value={reportingScheduleValue}
                  onChange={(next) => {
                    setReportingScheduleValue(next);
                    // Keep legacy field in sync for backward compat
                    setReportingCadence(next.cadence);
                  }}
                  disabled={!canEditProject}
                />
                <div className={styles.helpText}>{t("advanced.reportingCadenceHelp")}</div>
              </div>
            </div>

            {/* ── Budget & Currency (DEV-576) ── */}
            <div className={styles.sectionBox}>
              <div className={styles.sectionTitle}>{t("advanced.sectionBudget")}</div>
              <div className={styles.formRow}>
                <label className={styles.label}>{t("advanced.totalBudget")}</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.7 }}>
                    {getCurrency(budgetCurrency).symbol}
                  </span>
                  <input
                    className={styles.input}
                    type="number"
                    min="0"
                    step="any"
                    placeholder="0"
                    value={budgetEstimate}
                    onChange={(e) => setBudgetEstimate(e.target.value)}
                    disabled={!canEditProject}
                    style={{ flex: 1 }}
                  />
                </div>
                <div className={styles.helpText}>{t("advanced.totalBudgetHelp")}</div>
              </div>
              <div className={styles.formRow} style={{ marginTop: 12 }}>
                <label className={styles.label}>{t("advanced.currency")}</label>
                <select
                  className={styles.input}
                  value={budgetCurrency}
                  onChange={(e) => setBudgetCurrency(e.target.value)}
                  disabled={!canEditProject}
                >
                  {ALL_CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.symbol} {c.name}
                    </option>
                  ))}
                </select>
                <div className={styles.helpText}>{t("advanced.currencyHelp")}</div>
              </div>
            </div>

            {/* ── Workflow & Approvals (moved from Access & Permissions) ── */}
            <div className={styles.sectionBox}>
              <div className={styles.sectionTitle}>{t("advanced.sectionWorkflow")}</div>
              <div className={styles.helpText}>{t("advanced.workflowDesc")}</div>
              {canEditProject && (
                <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                  {projectWorkflowOptions.map((w) => {
                    const enabled = isProjectWorkflowEnabled(String(projectGuid), String(w.action) as any);
                    return (
                      <label
                        key={w.action}
                        className={styles.formRow}
                        style={{ alignItems: "flex-start", gap: 10, cursor: "pointer" }}
                        title={t(w.descKey)}
                      >
                        <input
                          type="checkbox"
                          checked={!!enabled}
                          onChange={(e) => {
                            const next = !!e.target.checked;
                            setProjectWorkflowEnabled(String(projectGuid), String(w.action) as any, next);
                            setWorkflowToggleVersion((v) => v + 1);
                          }}
                        />
                        <div style={{ display: "grid", gap: 2 }}>
                          <div style={{ fontWeight: 600 }}>{t(w.labelKey)}</div>
                          <div style={{ fontSize: 12, opacity: 0.8, lineHeight: 1.35 }}>{t(w.descKey)}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              <span style={{ display: "none" }}>{workflowToggleVersion}</span>
            </div>

            {/* ── Gantt ── */}
            <div className={styles.sectionBox}>
              <div className={styles.sectionTitle}>{t("advanced.sectionGantt")}</div>
              <div className={styles.formRow}>
                <label className={styles.label}>{t("advanced.ganttStartDate")}</label>
                <input
                  className={styles.input}
                  type="date"
                  value={scheduleStartDate || projectStartDate}
                  onChange={(e) => setScheduleStartDate(e.target.value)}
                />
                <div className={styles.helpText}>{t("advanced.ganttStartDateHelp")}</div>
              </div>
              <div className={styles.formRow} style={{ marginTop: 12 }}>
                <label className={styles.label}>{t("advanced.ganttColorTheme")}</label>
                <select
                  className={styles.input}
                  value={ganttColorTheme}
                  onChange={(e) => setGanttColorTheme(e.target.value)}
                >
                  <option value="match_theme">{t("advanced.ganttThemeMatch")}</option>
                  <option value="black">{t("advanced.ganttThemeBlack")}</option>
                  <option value="white">{t("advanced.ganttThemeWhite")}</option>
                </select>
                <div className={styles.helpText}>{t("advanced.ganttColorThemeHelp")}</div>
              </div>
              <div className={styles.formRow} style={{ marginTop: 12 }}>
                <label className={styles.label}>{t("advanced.workWeek")}</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  {([
                    { iso: 1, label: t("advanced.dayMon") },
                    { iso: 2, label: t("advanced.dayTue") },
                    { iso: 3, label: t("advanced.dayWed") },
                    { iso: 4, label: t("advanced.dayThu") },
                    { iso: 5, label: t("advanced.dayFri") },
                    { iso: 6, label: t("advanced.daySat") },
                    { iso: 7, label: t("advanced.daySun") },
                  ] as const).map(({ iso, label }) => {
                    const checked = workWeek === null || workWeek.includes(iso);
                    return (
                      <label key={iso} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer', fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (workWeek === null) {
                              // Switching from 7-day to explicit: uncheck this day
                              setWorkWeek([1, 2, 3, 4, 5, 6, 7].filter((d) => d !== iso));
                            } else if (checked) {
                              // Uncheck: remove day (but keep at least 1)
                              const next = workWeek.filter((d) => d !== iso);
                              setWorkWeek(next.length > 0 ? next : workWeek);
                            } else {
                              // Check: add day
                              setWorkWeek([...workWeek, iso].sort((a, b) => a - b));
                            }
                          }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
                <div className={styles.helpText}>{t("advanced.workWeekHelp")}</div>
              </div>
              <div className={styles.helpText} style={{ marginTop: 8 }}>{t("advanced.ganttPlaceholder")}</div>
            </div>
          </div>
        ) : null}

        <div className={styles.footer}>
        <button className={styles.secondaryButton} onClick={onClose}>
          Cancel
        </button>
        <button
          className={styles.primaryButton}
          onClick={handleSave}
          disabled={(!kpiTemplate && !isCategoryWeightsTotalOk) || !isAllSubKpiWeightsOk}
          title={(!kpiTemplate && !isCategoryWeightsTotalOk) ? t("validation.weightsSum", { total: categoryWeightsTotal }) : (!isAllSubKpiWeightsOk ? t("validation.subWeightsSum") : t("save"))}>
          {t("save")}
        </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}